// src/aggregation-operator-classifier.ts
import { AGGREGATION_OPERATORS } from "@vy/types";
import { isNullOrUndefined, raiseUnreachable } from "@vy/utils";
function checkIsTemporalAggregationOperator(operator, treatLastAsDaily = false) {
  if (isNullOrUndefined(operator)) {
    return false;
  }
  switch (operator) {
    case AGGREGATION_OPERATORS.Max:
    case AGGREGATION_OPERATORS.Min:
    case AGGREGATION_OPERATORS.First:
    case AGGREGATION_OPERATORS.Average:
      return true;
    case AGGREGATION_OPERATORS.Last:
      return !treatLastAsDaily;
    case AGGREGATION_OPERATORS.Sum:
    case AGGREGATION_OPERATORS.Count:
    case AGGREGATION_OPERATORS.Distinct:
      return false;
    default:
      return raiseUnreachable(operator);
  }
}

// src/billing-cycles-calculators/billing-interval-alignment-checker.ts
import { BILLING_INTERVALS } from "@vy/types";
import { raiseUnreachable as raiseUnreachable2 } from "@vy/utils";
function isBillingIntervalYearlyAligned(billingInterval, isCalendarAligned) {
  switch (billingInterval) {
    case BILLING_INTERVALS.Monthly:
    case BILLING_INTERVALS.BiMonthly:
    case BILLING_INTERVALS.Quarterly:
      return isCalendarAligned;
    case BILLING_INTERVALS.SixMonths:
    case BILLING_INTERVALS.Yearly:
      return false;
    default:
      return raiseUnreachable2(billingInterval);
  }
}

// src/billing-cycles-calculators/calculate-billing-cycle.ts
import { isNotNullOrUndefined } from "@vy/utils";
function getBillingCycleByStartDate(date, billingIntervalInMonths, isYearlyAligned, contractStartDate) {
  const { endTime } = getBillingCycle(date, billingIntervalInMonths, isYearlyAligned, contractStartDate);
  return {
    startTime: date,
    endTime
  };
}
function getBillingCycle(date, billingIntervalInMonths, isYearlyAligned, contractStartDate) {
  const calculationStart = getCalculationStart(date, isYearlyAligned, contractStartDate);
  const monthsSinceCalculationStart = date.diff(calculationStart, "month") + 1;
  const monthsFromCalculationStartToClosestBillingPeriod = Math.ceil(monthsSinceCalculationStart / billingIntervalInMonths) * billingIntervalInMonths;
  const startOfNextBillingPeriod = calculationStart.add(
    monthsFromCalculationStartToClosestBillingPeriod,
    "month"
  );
  const endOfCalculatedBillingPeriod = startOfNextBillingPeriod.subtract(1, "day").endOf("day");
  const startOfCalculatedBillingPeriod = startOfNextBillingPeriod.subtract(billingIntervalInMonths, "month").startOf("day");
  return {
    startTime: startOfCalculatedBillingPeriod,
    endTime: endOfCalculatedBillingPeriod
  };
}
function getCalculationStart(date, isYearlyAligned, contractStartDate) {
  return !isYearlyAligned && isNotNullOrUndefined(contractStartDate) ? contractStartDate : date.startOf("year");
}

// src/billing-cycles-calculators/calculate-billing-cycles-in-period.ts
import { mapVyDatePeriodToPeriod, vyDate as vyDate2 } from "@vy/utils";

// src/billing-settings-number-converter/index.ts
import { BILLING_INTERVALS as BILLING_INTERVALS2 } from "@vy/types";
import { raiseUnreachable as raiseUnreachable3 } from "@vy/utils";

// src/contract-end-date-calculator/index.ts
import { MAX_DURATION, UNLIMITED_DURATION, UnlimitedDurationSchema } from "@vy/types";
import { isNullOrUndefined as isNullOrUndefined2, vyDate } from "@vy/utils";
function getLastContractDay(startDate, duration) {
  if (duration < -1) {
    throw new Error("Invalid plan duration");
  }
  return UnlimitedDurationSchema.safeParse(duration).success ? null : startDate.add(duration, "month").subtract(1, "day").endOf("day");
}
function isUnlimitedDate(startDate, endDate) {
  if (isNullOrUndefined2(endDate)) {
    return true;
  }
  return vyDate(endDate).diff(vyDate(startDate), "month") >= MAX_DURATION;
}
function isUnlimitedDuration(duration) {
  return duration === UNLIMITED_DURATION || duration > MAX_DURATION;
}

// src/billing-settings-number-converter/index.ts
var MONTHS_IN_YEAR = 12;
function getBillingIntervalInMonths(billingInterval) {
  switch (billingInterval) {
    case BILLING_INTERVALS2.Monthly:
      return 1;
    case BILLING_INTERVALS2.BiMonthly:
      return 2;
    case BILLING_INTERVALS2.Quarterly:
      return 3;
    case BILLING_INTERVALS2.SixMonths:
      return 6;
    case BILLING_INTERVALS2.Yearly:
      return 12;
    default:
      return raiseUnreachable3(billingInterval);
  }
}
function getBillingIntervalsInYear(billingInterval) {
  return MONTHS_IN_YEAR / getBillingIntervalInMonths(billingInterval);
}
function getBillingCyclesPerDurationInMonths(billingInterval, planDuration) {
  const billingIntervalMonths = getBillingIntervalInMonths(billingInterval);
  if (isUnlimitedDuration(planDuration)) {
    return Math.floor(MONTHS_IN_YEAR / billingIntervalMonths);
  }
  return Math.floor(planDuration / billingIntervalMonths);
}

// src/billing-cycles-calculators/create-billing-cycles.ts
import { buildPeriod, isNotNullOrUndefined as isNotNullOrUndefined2 } from "@vy/utils";
function createBillingCycles(startDate, billingInterval, cyclesToCreate, endDateLimit, isCalendarAligned) {
  if (startDate.isAfter(endDateLimit)) {
    return [];
  }
  const billingIntervalInMonths = getBillingIntervalInMonths(billingInterval);
  const isYearlyAligned = isBillingIntervalYearlyAligned(billingInterval, isCalendarAligned);
  return Array.from({ length: cyclesToCreate }, (_, index) => {
    const startTime = startDate.add(index * billingIntervalInMonths, "month");
    const endTime = getAlignmentEndTime(billingIntervalInMonths, isYearlyAligned, startTime);
    const isAfterEndDateLimit = isNotNullOrUndefined2(endDateLimit) && endTime.isAfter(endDateLimit);
    const limitedEndDate = isAfterEndDateLimit ? endDateLimit : endTime;
    return buildPeriod(startTime.toDate(), limitedEndDate.toDate());
  });
}
function getAlignmentEndTime(billingIntervalInMonths, isYearlyAligned, startTime) {
  const newEndTime = startTime.add(billingIntervalInMonths - 1, "month");
  return isYearlyAligned ? newEndTime.endOf("month") : newEndTime.add(1, "month").subtract(1, "day").endOf("day");
}

// src/billing-cycles-calculators/calculate-billing-cycles-in-period.ts
function calculateBillingCyclesInPeriod(period, billingInterval, contractStartDate, isCalendarAligned) {
  const billingIntervalInMonths = getBillingIntervalInMonths(billingInterval);
  const isYearlyAligned = isBillingIntervalYearlyAligned(billingInterval, isCalendarAligned);
  const monthsBetweenPeriod = vyDate2(period.endTime).diff(period.startTime, "month") + 1;
  const billingIntervalsInPeriod = Math.ceil(monthsBetweenPeriod / billingIntervalInMonths);
  const firstBillingCycle = getBillingCycleByStartDate(
    vyDate2(period.startTime),
    billingIntervalInMonths,
    isYearlyAligned,
    contractStartDate
  );
  const secondBillingCycleStartDate = vyDate2(firstBillingCycle.endTime).add(1, "day").startOf("day");
  const calculatedBillingCycles = createBillingCycles(
    secondBillingCycleStartDate,
    billingInterval,
    billingIntervalsInPeriod,
    vyDate2(period.endTime),
    isCalendarAligned
  );
  const billingCycles = [mapVyDatePeriodToPeriod(firstBillingCycle), ...calculatedBillingCycles];
  return billingCycles.filter(
    (billingPeriod) => vyDate2(billingPeriod.endTime).isAfter(vyDate2(billingPeriod.startTime))
  );
}

// src/build-invoice-review-status/build-invoice-review-status.ts
import { INVOICE_REVIEW_STATUSES, INVOICE_TYPES, PAYMENT_TERMS } from "@vy/types";
import {
  isDateBetween as isDateBetween2,
  isNotEmptyArray,
  now as now2,
  raiseUnreachable as raiseUnreachable4,
  validateNotEmptyArray,
  vyDate as vyDate3
} from "@vy/utils";

// src/build-invoice-review-status/max-periods-with-product-types-getter.ts
import { extract, findMaxPeriodSpan } from "@vy/utils";
function getMaxPeriodsWithPaymentTermsSpan(periodsWithPaymentTerms) {
  const periods = extract(periodsWithPaymentTerms, "period");
  return findMaxPeriodSpan(periods);
}

// src/build-invoice-review-status/previous-billing-cycle-days-amount-getter.ts
function getPreviousBillingCycleDayAmount(billingInterval) {
  const DEFAULT_MONTH_DAY_AMOUNT = 30;
  return billingInterval * DEFAULT_MONTH_DAY_AMOUNT;
}

// src/build-invoice-review-status/active-periods-by-product-type-filter.ts
import { isDateBetween, now } from "@vy/utils";
function filterActivePeriodsByPaymentTerm(periodsWithPaymentTerms, paymentTerm) {
  const nowDate = now();
  const activePeriods = periodsWithPaymentTerms.filter(({ period }) => isDateBetween(nowDate, period));
  return activePeriods.filter((period) => period.paymentTerm === paymentTerm);
}

// src/build-invoice-review-status/build-invoice-review-status.ts
function buildInvoiceReviewStatus(periodsWithPaymentTerms, billingInterval, invoiceType) {
  validateNotEmptyArray(periodsWithPaymentTerms, "periodsWithPaymentTerms");
  switch (invoiceType) {
    case INVOICE_TYPES.Prepayment:
      return buildPrepaidInvoiceReviewStatus(periodsWithPaymentTerms, billingInterval);
    case INVOICE_TYPES.Hybrid:
      return buildHybridInvoiceReviewStatus(periodsWithPaymentTerms, billingInterval);
    case INVOICE_TYPES.Postpayment:
      return buildPostpaidInvoiceReviewStatus(periodsWithPaymentTerms);
    default:
      return raiseUnreachable4(invoiceType);
  }
}
function buildHybridInvoiceReviewStatus(periodsWithPaymentTerms, billingInterval) {
  const activePrepaymentPeriods = filterActivePeriodsByPaymentTerm(
    periodsWithPaymentTerms,
    PAYMENT_TERMS.Prepayment
  );
  if (activePrepaymentPeriods.length > 0) {
    const prepaymentPeriods = periodsWithPaymentTerms.filter(
      (periodWithPaymentTerm) => periodWithPaymentTerm.paymentTerm === PAYMENT_TERMS.Prepayment
    );
    validateNotEmptyArray(prepaymentPeriods, "prepaymentPeriods");
    return buildPrepaidInvoiceReviewStatus(prepaymentPeriods, billingInterval);
  }
  const postPaidPeriods = periodsWithPaymentTerms.filter(
    (periodWithPaymentTerm) => periodWithPaymentTerm.paymentTerm === PAYMENT_TERMS.Postpayment
  );
  if (!isNotEmptyArray(postPaidPeriods)) {
    return INVOICE_REVIEW_STATUSES.Future;
  }
  return buildPostpaidInvoiceReviewStatus(postPaidPeriods);
}
function buildPostpaidInvoiceReviewStatus(periodsWithPaymentTerms) {
  const nowDate = now2();
  const maxPeriodSpan = getMaxPeriodsWithPaymentTermsSpan(periodsWithPaymentTerms);
  const isBillingCycleNotStarted = vyDate3(nowDate).isBefore(maxPeriodSpan.startTime);
  if (isBillingCycleNotStarted) {
    return INVOICE_REVIEW_STATUSES.Future;
  }
  const isInsideBillingCycle = isDateBetween2(nowDate, maxPeriodSpan);
  return isInsideBillingCycle ? INVOICE_REVIEW_STATUSES.Draft : INVOICE_REVIEW_STATUSES.InReview;
}
function buildPrepaidInvoiceReviewStatus(periodsWithPaymentTerms, billingInterval) {
  const nowDate = now2();
  const maxPeriodSpan = getMaxPeriodsWithPaymentTermsSpan(periodsWithPaymentTerms);
  const previousBillingCycleLength = getPreviousBillingCycleDayAmount(billingInterval);
  const inReviewStartDate = vyDate3(maxPeriodSpan.startTime).subtract(previousBillingCycleLength, "day");
  const isInReview = vyDate3(nowDate).isAfter(inReviewStartDate);
  return isInReview ? INVOICE_REVIEW_STATUSES.InReview : INVOICE_REVIEW_STATUSES.Future;
}

// src/build-Issue-and-due-dates.ts
import {
  getPaymentDueDays
} from "@vy/types";
import { isNotNullOrUndefined as isNotNullOrUndefined3, vyDate as vyDate6 } from "@vy/utils";

// src/get-due-day.ts
import { INVOICE_DUE_DATE_VALUES } from "@vy/types";
import { validateNotNullOrUndefined, vyDate as vyDate4 } from "@vy/utils";
function buildDueDate(issuedAt, paymentDueDays, isTrial = false) {
  validateNotNullOrUndefined(issuedAt, "issuedAt");
  if (isTrial) {
    return vyDate4(issuedAt).toDate();
  }
  return paymentDueDays === INVOICE_DUE_DATE_VALUES.EOM ? vyDate4(issuedAt).endOf("month").toDate() : vyDate4(issuedAt).add(paymentDueDays, "day").toDate();
}

// src/get-issued-at.ts
import { vyDate as vyDate5 } from "@vy/utils";
function buildIssuedAtNow() {
  return vyDate5().startOf("day").toDate();
}

// src/build-Issue-and-due-dates.ts
function buildIssueAndDueDates(expectedIssuedAt, accountSettings, customerSettings, invoicingDate, isApproved, forcedIssueAt, isTrial) {
  const addIssueAndDueDates = shouldAddIssueAndDueDates(
    accountSettings?.invoicingSettings,
    isApproved ?? false
  );
  if (!addIssueAndDueDates) {
    return { issuedAt: null, paymentDueDays: null, dueDate: null };
  }
  const issuedAt = getIssuedAt(expectedIssuedAt, accountSettings, invoicingDate, forcedIssueAt);
  const paymentDueDays = getPaymentDueDays(customerSettings?.paymentDueDays, accountSettings, issuedAt);
  const dueDate = buildDueDate(issuedAt, paymentDueDays, isTrial);
  return {
    issuedAt,
    paymentDueDays,
    dueDate
  };
}
function getIssuedAt(expectedIssuedAt, accountSettings, invoicingDate, forcedIssueAt) {
  if (isNotNullOrUndefined3(forcedIssueAt)) {
    return forcedIssueAt;
  }
  const setIssuedAtToNow = shouldIssueOnApproval(accountSettings.invoicingSettings) && vyDate6().isAfter(expectedIssuedAt);
  return setIssuedAtToNow ? buildIssuedAtNow() : invoicingDate;
}
function shouldIssueOnApproval(invoicingSettings) {
  return isNotNullOrUndefined3(invoicingSettings) && invoicingSettings.issueOnApproval;
}
function shouldAddIssueAndDueDates(invoicingSettings, isApproved) {
  return shouldIssueOnApproval(invoicingSettings) && isApproved || !shouldIssueOnApproval(invoicingSettings);
}

// src/calculate-fixed-product.ts
import { PRICING_MODELS as PRICING_MODELS4, supportsProration } from "@vy/types";
import {
  getDaysBetween,
  isNotNullOrUndefined as isNotNullOrUndefined5,
  mapPeriodToVyDatePeriod,
  raise,
  raiseUnreachable as raiseUnreachable5,
  vyDate as vyDate7,
  withDefault
} from "@vy/utils";

// src/products-filter/index.ts
import { BILLING_INTERVALS as BILLING_INTERVALS3, FIXED_PRODUCT_TYPES } from "@vy/types";
import {
  first,
  isNotNullOrUndefined as isNotNullOrUndefined4,
  isNullOrUndefined as isNullOrUndefined4,
  isSingleItem,
  validateNotNullOrUndefined as validateNotNullOrUndefined2
} from "@vy/utils";
import { isEmptyArray, every } from "@vy/utils";
import { isBoolean } from "lodash";

// src/pricing-filter/index.ts
import {
  CommercialTermsPricingSchema,
  FixedPricingSchema,
  OneTimePricingSchema,
  PRICING_MODELS,
  SubscriptionPricingSchema,
  UsagePricingSchema
} from "@vy/types";
import { isNullOrUndefined as isNullOrUndefined3 } from "@vy/utils";
function isTiersPricing(pricing) {
  return pricing.pricingModel === PRICING_MODELS.Tiers;
}
function isFlatRatePricing(pricing) {
  return isTiersPricing(pricing) ? Boolean(pricing.priceConfiguration.isFlatRate) : false;
}
function getTierFlatRate(pricing, tierIndex) {
  if (!isTieredSubscriptionPricing(pricing) && !isFlatRatePricing(pricing)) {
    return void 0;
  }
  const tiers = pricing.priceConfiguration.tiers;
  if (isNullOrUndefined3(tierIndex)) {
    return tiers.at(0)?.flatRate;
  }
  return tiers.at(tierIndex)?.flatRate;
}
function isTieredSubscriptionPricing(pricing) {
  return pricing.pricingModel === PRICING_MODELS.TieredSubscription;
}
function isPerUnitPricing(pricing) {
  return pricing.pricingModel === PRICING_MODELS.PerUnit;
}
function isRevSharePricing(pricing) {
  return isTiersPricing(pricing) && pricing.priceConfiguration.isRevShare;
}
function isFixedPricing(pricing) {
  return FixedPricingSchema.safeParse(pricing).success;
}
function isUsagePricing(pricing) {
  return UsagePricingSchema.safeParse(pricing).success;
}
function isOneTimePricing(pricing) {
  return OneTimePricingSchema.safeParse(pricing).success;
}
function isSubscriptionPricing(pricing) {
  return SubscriptionPricingSchema.safeParse(pricing).success;
}
function isCommercialTermsPricing(pricing) {
  return CommercialTermsPricingSchema.safeParse(pricing).success;
}
function validateUsagePricing(pricing) {
  if (!isPerUnitPricing(pricing) && !isTiersPricing(pricing) && !isTieredSubscriptionPricing(pricing)) {
    throw new Error("Unsupported pricing model");
  }
}
function validatePerUnitPricing(pricing) {
  if (!isPerUnitPricing(pricing)) {
    throw new Error("Pricing is not PerUnitPricing");
  }
}

// src/product-variants-filter/index.ts
import { PRICING_MODELS as PRICING_MODELS2 } from "@vy/types";
function getTiersProductVariants(variants) {
  return variants.filter(isTiersProductVariant);
}
function getPerUnitProductVariants(variants) {
  return variants.filter(isPerUnitProductVariant);
}
function isSubscriptionProductVariant(variant) {
  return isVariantOfPricingModel(variant, PRICING_MODELS2.Subscription);
}
function isCommissionProductVariant(variant) {
  return isVariantOfPricingModel(variant, PRICING_MODELS2.Commission);
}
function isOneTimeProductVariant(variant) {
  return isVariantOfPricingModel(variant, PRICING_MODELS2.FixedOneTime);
}
function isCommercialTermsProductVariant(variant) {
  return isVariantOfPricingModel(variant, PRICING_MODELS2.CommercialTerms);
}
function isFixedDeductionProductVariant(variant) {
  return isVariantOfPricingModel(variant, PRICING_MODELS2.FixedDeduction);
}
function isPercentageDeductionProductVariant(variant) {
  return isVariantOfPricingModel(variant, PRICING_MODELS2.PercentageDeduction);
}
function isRefundProductVariant(variant) {
  return isVariantOfPricingModel(variant, PRICING_MODELS2.Refund);
}
function isUsageProductVariant(variant) {
  return isTiersProductVariant(variant) || isPerUnitProductVariant(variant);
}
function isTiersProductVariant(variant) {
  return isVariantOfPricingModel(variant, PRICING_MODELS2.Tiers);
}
function isTieredSubscriptionProductVariant(variant) {
  return isVariantOfPricingModel(variant, PRICING_MODELS2.TieredSubscription);
}
function isRevShareProductVariant(variant) {
  return isTiersProductVariant(variant) && variant.pricing.priceConfiguration?.isRevShare;
}
function isMeteredCreditProductVariant(variant) {
  return isPerUnitProductVariant(variant) && variant.pricing.priceConfiguration?.isMeteredCredit;
}
function isTierLockedProductVariant(variant) {
  return (isTieredSubscriptionProductVariant(variant) || isTiersProductVariant(variant)) && !!variant.pricing.priceConfiguration?.tierLock;
}
function isTiersVolumeProductVariant(variant) {
  return isTiersProductVariant(variant) && variant.pricing.priceConfiguration.isVolume;
}
function isPerUnitProductVariant(variant) {
  return isVariantOfPricingModel(variant, PRICING_MODELS2.PerUnit);
}
function isVariantOfPricingModel(variant, pricingModel) {
  return variant.pricing.pricingModel === pricingModel;
}

// src/products-filter/products-filter-consts.ts
import { PRICING_MODELS as PRICING_MODELS3 } from "@vy/types";
var USAGE_PRICING_MODELS = [
  PRICING_MODELS3.Tiers,
  PRICING_MODELS3.PerUnit,
  PRICING_MODELS3.TieredSubscription
];
var FIXED_PRICING_MODELS = [
  PRICING_MODELS3.FixedOneTime,
  PRICING_MODELS3.Subscription,
  PRICING_MODELS3.CommercialTerms,
  PRICING_MODELS3.Commission,
  PRICING_MODELS3.Refund,
  PRICING_MODELS3.FixedDeduction,
  PRICING_MODELS3.PercentageDeduction
];

// src/products-filter/products-filter-types/product-type.ts
var PRODUCT_TYPES = {
  FIXED: "Fixed",
  USAGE: "Usage"
};

// src/products-filter/index.ts
function isSubscriptionProduct(product) {
  return isSingleItem(product.variants) && isSubscriptionProductVariant(product.variants[0]);
}
function isOneTimeProduct(product) {
  return isSingleItem(product.variants) && isOneTimeProductVariant(product.variants[0]);
}
function isCommercialTermsProduct(product) {
  return isSingleItem(product.variants) && isCommercialTermsProductVariant(product.variants[0]);
}
function isContractLengthProduct(product) {
  return isCommercialTermsProduct(product) && isCommercialTermsProductContractLength(product);
}
function isCreditProduct(product) {
  return isOneTimeProduct(product) && product.variants.some(
    ({ settings }) => isNotNullOrUndefined4(settings) && settings.productType === FIXED_PRODUCT_TYPES.Credit
  );
}
function isRefundProduct(product) {
  return isSingleItem(product.variants) && isRefundProductVariant(product.variants[0]);
}
function filterCommercialTermsProducts(products) {
  return products.filter(isCommercialTermsProduct);
}
function filterSubscriptionProducts(products) {
  return products.filter(isSubscriptionProduct);
}
function isCommissionProduct(product) {
  return isSingleItem(product.variants) && isCommissionProductVariant(product.variants[0]);
}
function filterCommissionProducts(products) {
  return products.filter(isCommissionProduct);
}
function filterUsageProducts(products) {
  return isNullOrUndefined4(products) ? [] : filterProductByType(products, PRODUCT_TYPES.USAGE);
}
function filterFixedProducts(products) {
  return isNullOrUndefined4(products) ? [] : filterProductByType(products, PRODUCT_TYPES.FIXED);
}
function filterPromotionProducts(products) {
  return isNullOrUndefined4(products) ? [] : products.filter(
    (product) => isFixedDeductionProductOnly(product) || isPercentageDeductionProductOnly(product)
  );
}
function filterOneTimeProducts(products) {
  return products.filter(isOneTimeProduct);
}
function isUsageProduct(product) {
  return isOfProductType(product, PRODUCT_TYPES.USAGE);
}
function isFixedProduct(product) {
  return isNotNullOrUndefined4(product) && isOfProductType(product, PRODUCT_TYPES.FIXED) && isSingleItem(product.variants);
}
function isNonRelativisticFixedProduct(product) {
  return isFixedProduct(product) && !isCommissionProduct(product) && !isPercentageDeductionProductOnly(product);
}
function isTiersUsageProductOnly(product) {
  return isOfProductType(product, PRODUCT_TYPES.USAGE) && every(product.variants, isTiersProductVariant);
}
function isTieredSubscriptionUsageProductOnly(product) {
  return isOfProductType(product, PRODUCT_TYPES.USAGE) && every(product.variants, isTieredSubscriptionProductVariant);
}
function isTiersFlatRateProduct(product) {
  if (!isOfProductType(product, PRODUCT_TYPES.USAGE)) {
    return false;
  }
  return product.variants?.some(
    (variant) => isTiersProductVariant(variant) && isFlatRatePricing(variant.pricing)
  ) ?? false;
}
function usageProductEmitsFixedCharges(product) {
  return isTiersFlatRateProduct(product);
}
function isRevShareProductOnly(product) {
  return isOfProductType(product, PRODUCT_TYPES.USAGE) && every(product.variants, isRevShareProductVariant);
}
function isMeteredCreditProductOnly(product) {
  return isOfProductType(product, PRODUCT_TYPES.USAGE) && every(product.variants, isMeteredCreditProductVariant);
}
function isNotMeteredCreditProductOnly(product) {
  return isOfProductType(product, PRODUCT_TYPES.USAGE) && !every(product.variants, isMeteredCreditProductVariant);
}
function isPerUnitUsageProductOnly(product) {
  return isOfProductType(product, PRODUCT_TYPES.USAGE) && every(product.variants, isPerUnitProductVariant);
}
function isFixedOneTimeProductOnly(product) {
  return isOfProductType(product, PRODUCT_TYPES.FIXED) && every(product.variants, isOneTimeProductVariant);
}
function isSubscriptionProductOnly(product) {
  return isOfProductType(product, PRODUCT_TYPES.FIXED) && every(product.variants, isSubscriptionProductVariant);
}
function isCommercialTermsProductOnly(product) {
  return isOfProductType(product, PRODUCT_TYPES.FIXED) && every(product.variants, isCommercialTermsProductVariant);
}
function isRefundProductOnly(product) {
  return isOfProductType(product, PRODUCT_TYPES.FIXED) && every(product.variants, isRefundProductVariant);
}
function isFixedDeductionProductOnly(product) {
  return isOfProductType(product, PRODUCT_TYPES.FIXED) && every(product.variants, isFixedDeductionProductVariant);
}
function isPercentageDeductionProductOnly(product) {
  return isOfProductType(product, PRODUCT_TYPES.FIXED) && every(product.variants, isPercentageDeductionProductVariant);
}
function isCommissionProductOnly(product) {
  return isOfProductType(product, PRODUCT_TYPES.FIXED) && every(product.variants, isCommissionProductVariant);
}
function isSingleVariantProduct(product) {
  return isSingleItem(product.variants);
}
function getProductType(product) {
  validateNotNullOrUndefined2(product.variants);
  const variantsProductTypes = product.variants.map(({ pricing }) => {
    validateNotNullOrUndefined2(pricing.pricingModel);
    return getProductTypeFromPricingModel(pricing.pricingModel);
  });
  const uniqueVariantsProductTypes = Array.from(new Set(variantsProductTypes));
  if (isEmptyArray(uniqueVariantsProductTypes)) {
    throw new Error("Empty variants");
  }
  if (uniqueVariantsProductTypes.length > 1) {
    throw new Error("Heterogeneous Variants");
  }
  return uniqueVariantsProductTypes[0];
}
function filterCommitmentProducts(products) {
  return products.filter((product) => isCommitmentProduct(product));
}
function filterOutCommissionProducts(products) {
  return products.filter((product) => !isCommissionProduct(product));
}
function getCommitmentUsageProducts(products) {
  return products.filter(isCommitmentUsageProduct);
}
function isNonCommitmentUsageProduct(product) {
  return !isCommitmentUsageProduct(product);
}
function isCommitmentUsageProduct(product) {
  return isUsageProduct(product) && isCommitmentProduct(product);
}
function isCommitmentProduct(product) {
  return isNotNullOrUndefined4(product?.commitment);
}
function getFixedProductPricingModel(product) {
  return product.variants[0].pricing.pricingModel;
}
function getProductTypeFromPricingModel(pricingModel) {
  if (FIXED_PRICING_MODELS.includes(pricingModel)) {
    return PRODUCT_TYPES.FIXED;
  }
  if (USAGE_PRICING_MODELS.includes(pricingModel)) {
    return PRODUCT_TYPES.USAGE;
  }
  throw new Error("Unknown pricing model");
}
function filterProductByType(products, type) {
  return products.filter(
    (product) => isOfProductType(product, type)
  );
}
function isOfProductType(product, type) {
  return isNotNullOrUndefined4(product) && getProductType(product) === type;
}
function isTiersVolumeProduct(product) {
  return isTiersUsageProductOnly(product) && every(product.variants, isTiersVolumeProductVariant);
}
function isPromotionProduct(product) {
  return isFixedDeductionProductOnly(product) || isPercentageDeductionProductOnly(product);
}
function isNotPromotionProduct(product) {
  return !isPromotionProduct(product);
}
function isShouldCreateSeparateInvoiceInvoiceProduct(product) {
  return "shouldCreateSeparateInvoice" in product.variants[0].pricing.priceConfiguration && isBoolean(product.variants[0].pricing.priceConfiguration.shouldCreateSeparateInvoice) && product.variants[0].pricing.priceConfiguration.shouldCreateSeparateInvoice;
}
function isUpfrontPayProduct(product) {
  return "upfrontPay" in product.variants[0].pricing.priceConfiguration && isBoolean(product.variants[0].pricing.priceConfiguration.upfrontPay) && product.variants[0].pricing.priceConfiguration.upfrontPay;
}
function getFixedPricingModels() {
  return FIXED_PRICING_MODELS;
}
function getUsagePricingModels() {
  return USAGE_PRICING_MODELS;
}
function isCommercialTermsProductContractLength(product) {
  const isYearlyInterval = product.billingInterval === BILLING_INTERVALS3.Yearly;
  const variant = first(product.variants);
  validateNotNullOrUndefined2(variant, "Variant is not defined");
  const isNonInstallments = isNullOrUndefined4(variant.pricing.priceConfiguration.installments);
  return isYearlyInterval && isNonInstallments;
}
function isCommercialTermsWithoutScheduledInstallments(product) {
  return isCommercialTermsProduct(product) && !isScheduledInstallmentsProduct(product);
}
function isScheduledInstallmentsProduct(product) {
  if (!isCommercialTermsProduct(product)) {
    return false;
  }
  const variant = first(product.variants);
  return isNotNullOrUndefined4(variant?.pricing.priceConfiguration.scheduledInstallments);
}

// src/calculate-fixed-product.ts
function calculateFixedProduct(invoiceBillingCycle, product, billingData) {
  switch (product.variants[0].pricing.pricingModel) {
    case PRICING_MODELS4.FixedOneTime:
    case PRICING_MODELS4.CommercialTerms:
    case PRICING_MODELS4.Refund:
      return product.variants[0].pricing.priceConfiguration.price;
    case PRICING_MODELS4.FixedDeduction:
      return -product.variants[0].pricing.priceConfiguration.amountToDeduct;
    case PRICING_MODELS4.PercentageDeduction:
    case PRICING_MODELS4.Commission:
      return 0;
    case PRICING_MODELS4.Subscription: {
      return isSubscriptionProductOnly(product) ? isNotNullOrUndefined5(billingData) ? calculateSubscriptionProductPrice(invoiceBillingCycle, billingData, product) : raise("Billing data is required") : raise("Unsupported product type");
    }
    default:
      return raiseUnreachable5(product.variants[0].pricing);
  }
}
function calculateSubscriptionProductPrice(invoiceBillingCycle, billingData, product) {
  const seats = withDefault(product.variants[0].pricing.priceConfiguration.seats, 1);
  const basePrice = product.variants[0].pricing.priceConfiguration.price * seats;
  if (isSubscriptionProductOnly(product) && product.variants[0].pricing.priceConfiguration.upfrontPay) {
    return basePrice * getBillingCyclesPerDurationInMonths(billingData.billingInterval, billingData.duration);
  }
  const shouldProrate = product.proRata && supportsProration(product.variants[0].pricing.pricingModel);
  return shouldProrate ? calculateProRataPrice(invoiceBillingCycle, billingData, basePrice, product.isCalendarAligned) : calculateNormalPrice(invoiceBillingCycle, billingData, basePrice, product.isCalendarAligned);
}
function calculateProRataPrice(invoiceBillingCycle, billingData, basePrice, isCalendarAligned) {
  const invoiceStartDate = vyDate7(invoiceBillingCycle.startTime);
  const billingIntervalInMonths = getBillingIntervalInMonths(billingData.billingInterval);
  const isYearlyAligned = isBillingIntervalYearlyAligned(billingData.billingInterval, isCalendarAligned);
  const fullBillingCycle = getBillingCycle(
    invoiceStartDate,
    billingIntervalInMonths,
    isYearlyAligned,
    invoiceStartDate
  );
  const daysInFullBillingCycle = getDaysBetween(fullBillingCycle);
  const daysInInvoiceBillingCycle = getDaysBetween(mapPeriodToVyDatePeriod(invoiceBillingCycle));
  const ratioBetweenFullAndInvoiceBillingCycle = daysInInvoiceBillingCycle / daysInFullBillingCycle;
  const fullPrice = basePrice * billingIntervalInMonths;
  const proRataPrice = fullPrice * ratioBetweenFullAndInvoiceBillingCycle;
  return Math.min(proRataPrice, fullPrice);
}
function calculateNormalPrice(invoiceBillingCycle, billingData, basePrice, isCalendarAligned) {
  const proRataPrice = calculateProRataPrice(invoiceBillingCycle, billingData, basePrice, isCalendarAligned);
  const monthsCrossedInBillingCycle = Math.ceil(proRataPrice / basePrice);
  return monthsCrossedInBillingCycle * basePrice;
}

// src/calculate-usage-line-item-discount-price.ts
import { DISCOUNT_TYPES } from "@vy/types";
import { findRequired, getMapEntries, isNullOrUndefined as isNullOrUndefined5, raise as raise2, sumBy as sumBy3 } from "@vy/utils";

// src/usage-variant-calculator/usage-variant-calculators/tiers-item-calculator.ts
import { isBetweenInclusive as isBetweenInclusive2, isNotNullOrUndefined as isNotNullOrUndefined6, sumBy as sumBy2, withDefault as withDefault2 } from "@vy/utils";

// src/tiers-calculator.ts
import { isBetweenInclusive, sumBy } from "@vy/utils";
function calculateTiersUnitNumber(unitsCount, from, to) {
  if (unitsCount < from) {
    return 0;
  }
  return isBetweenInclusive(unitsCount, from, to) ? unitsCount - from + 1 : to - from + 1;
}
function calculateTiersPrice(tiers, units) {
  return sumBy(tiers, ({ from, to, price }) => {
    const tierUnits = calculateTiersUnitNumber(units, from, to);
    return tierUnits * price;
  });
}

// src/usage-variant-calculator/usage-variant-calculators/tiers-item-calculator.ts
var TiersItemCalculator = class {
  calculate(variant, measurements) {
    const { tiers, isVolume, isFlatRate } = variant.pricing.priceConfiguration;
    const units = sumBy2(measurements, "value");
    const price = this.calculateTiersPrice(tiers, units, isVolume, isFlatRate);
    const tiersCharges = this.getTiersChargesInfo(variant, units);
    return {
      price,
      units,
      tiers: tiersCharges
    };
  }
  calculateVolumeTiersPrice(tiers, totalUnitsConsumed, isFlatRate) {
    if (totalUnitsConsumed === 1) {
      const firstFlatRate = getFlatRateCharge(tiers[0].flatRate, isFlatRate);
      return tiers[0].price + firstFlatRate;
    }
    const inBoundsTier = tiers.find(
      (tier) => tier.from < totalUnitsConsumed && tier.to >= totalUnitsConsumed
    );
    const flatRateCharge = getFlatRateCharge(inBoundsTier?.flatRate, isFlatRate);
    return isNotNullOrUndefined6(inBoundsTier) ? inBoundsTier.price * totalUnitsConsumed + flatRateCharge : 0;
  }
  calculateNormalTiersPrice(tiers, totalUnitsConsumed, isFlatRate) {
    return sumBy2(tiers, ({ price, to, from, flatRate }) => {
      const flatRateCharge = getFlatRateCharge(flatRate, isFlatRate);
      if (to <= totalUnitsConsumed) {
        return price * (to - from + 1) + flatRateCharge;
      }
      return isBetweenInclusive2(totalUnitsConsumed, from, to) ? price * (totalUnitsConsumed - from + 1) + flatRateCharge : 0;
    });
  }
  calculateTiersPrice(tiers, totalUnitsConsumed, isVolume, isFlatRate) {
    return isVolume ? this.calculateVolumeTiersPrice(tiers, totalUnitsConsumed, isFlatRate) : this.calculateNormalTiersPrice(tiers, totalUnitsConsumed, isFlatRate);
  }
  getTiersChargesInfo(productVariant, totalProductVariantUnits) {
    const { tiers } = productVariant.pricing.priceConfiguration;
    const isVolume = productVariant.pricing.priceConfiguration.isVolume;
    const isFlatRate = productVariant.pricing.priceConfiguration.isFlatRate;
    return tiers.map(
      (tier, tierIndex) => this.getTierTierUsageData(tierIndex, tier, totalProductVariantUnits, isVolume, isFlatRate)
    );
  }
  getTierTierUsageData(tierIndex, tier, totalProductVariantUnits, isVolume, isFlatRate) {
    return isVolume ? this.getVolumeTierTierUsageData(tierIndex, tier, totalProductVariantUnits, isFlatRate) : this.getNormalTierTierUsageData(tierIndex, tier, totalProductVariantUnits, isFlatRate);
  }
  getVolumeTierTierUsageData(tierIndex, tier, totalProductVariantUnits, isFlatRate) {
    if (!(tier.from < totalProductVariantUnits && tier.to >= totalProductVariantUnits)) {
      return {
        periodUnits: 0,
        price: 0,
        tierIndex,
        discountConfiguration: null,
        chunkSize: 1
      };
    }
    const flatRateCharge = getFlatRateCharge(tier.flatRate, isFlatRate);
    const price = totalProductVariantUnits * tier.price + flatRateCharge;
    return {
      periodUnits: totalProductVariantUnits,
      price,
      tierIndex,
      discountConfiguration: null,
      chunkSize: 1
    };
  }
  getNormalTierTierUsageData(tierIndex, tier, totalProductVariantUnits, isFlatRate) {
    const periodUnits = calculateTiersUnitNumber(totalProductVariantUnits, tier.from, tier.to);
    const flatRateCharge = getFlatRateCharge(tier.flatRate, isFlatRate);
    const price = periodUnits * tier.price + flatRateCharge;
    return {
      periodUnits,
      price,
      tierIndex,
      discountConfiguration: null,
      chunkSize: withDefault2(tier.chunkSize, 1)
    };
  }
};
function getFlatRateCharge(flatRate, isFlatRateEnabled) {
  return isFlatRateEnabled ? flatRate ?? 0 : 0;
}

// src/calculate-usage-line-item-discount-price.ts
function calculateUsageLineItemDiscountPrice(tiersUsageMap, lineItemProduct) {
  return getMapEntries(tiersUsageMap).reduce((priceAccumulator, [productVariantId, currentTiersUsage]) => {
    const productVariant = findRequired(
      lineItemProduct.variants,
      (variant) => variant.productVariantId === productVariantId
    );
    if (isPerUnitProductVariant(productVariant)) {
      const [firstTierUsage] = currentTiersUsage;
      return priceAccumulator + calculatePerUnitProductVariantPrice(productVariant, firstTierUsage);
    }
    if (isTiersProductVariant(productVariant)) {
      const amountToAdd = calculateTiersProductVariantPrice(productVariant, currentTiersUsage);
      return priceAccumulator + amountToAdd;
    }
    return raise2(`Unsupported pricing model: ${productVariant.pricing.pricingModel}`);
  }, 0);
}
function calculateTiersProductVariantPrice(productVariant, tiersUsageData) {
  const { tiers, isVolume, isFlatRate } = productVariant.pricing.priceConfiguration;
  const tiersWithDiscount = buildTiersWithDiscount(tiers, tiersUsageData);
  const totalProductVariantUnitsConsumed = sumBy3(tiersUsageData, (item) => item.periodUnits);
  return new TiersItemCalculator().calculateTiersPrice(
    tiersWithDiscount,
    totalProductVariantUnitsConsumed,
    isVolume,
    isFlatRate
  );
}
function buildTiersWithDiscount(tiers, tiersUsageData) {
  return tiersUsageData.map(({ discountConfiguration, tierIndex }) => {
    if (discountConfiguration?.type === DISCOUNT_TYPES.Percentage) {
      raise2("Percentage discounts are not supported for tiers product variants");
    }
    const tier = tiers[tierIndex];
    const discountPrice = discountConfiguration?.amount;
    return {
      ...tier,
      price: isNullOrUndefined5(discountPrice) ? tier.price : discountPrice
    };
  });
}
function calculatePerUnitProductVariantPrice(productVariant, tierUsageData) {
  if (tierUsageData.discountConfiguration?.type === DISCOUNT_TYPES.Percentage) {
    raise2("Percentage discounts are not supported for per unit product variants");
  }
  const discountPrice = tierUsageData.discountConfiguration?.amount;
  const perUnitPrice = productVariant.pricing.priceConfiguration.price;
  return isNullOrUndefined5(discountPrice) ? perUnitPrice * tierUsageData.periodUnits : discountPrice * tierUsageData.periodUnits;
}

// src/commitment/get-active-commitment.ts
import { isNotNullOrUndefined as isNotNullOrUndefined7, isNullOrUndefined as isNullOrUndefined6 } from "@vy/utils";
function getActiveCommitment(product, productConfig) {
  const commitment = product.commitment ?? productConfig?.commitment;
  if (isNullOrUndefined6(commitment)) {
    return null;
  }
  return isNotNullOrUndefined7(productConfig) ? productConfig.commitment : product.commitment;
}

// src/commitment-price-calculator.ts
import { uniqueBy, sumBy as sumBy4, isNotNullOrUndefined as isNotNullOrUndefined8 } from "@vy/utils";
function getTotalCommitment(lineItems) {
  const commitments = lineItems.map(({ lineItemProduct }) => lineItemProduct.commitment).filter(isNotNullOrUndefined8);
  const uniqueCommitments = uniqueBy(
    commitments,
    (commitment) => `${commitment.amount}-${commitment.type}-${commitment.replenishmentInterval}`
  );
  return sumBy4(uniqueCommitments, ({ consumption }) => consumption);
}

// src/contracts/get-contract-first-billing-cycle.ts
import { vyDate as vyDate8 } from "@vy/utils";
function getContractFirstBillingCycle(contractStartDate, billingInterval) {
  const contractStartDay = vyDate8(contractStartDate);
  const billingIntervalInMonths = getBillingIntervalInMonths(billingInterval);
  const invoiceEndDate = contractStartDay.add(billingIntervalInMonths, "month").subtract(1, "month").endOf("month").toDate();
  return {
    startTime: contractStartDate,
    endTime: invoiceEndDate
  };
}

// src/contracts/get-contract-product-price.ts
import { PRICING_MODELS as PRICING_MODELS5 } from "@vy/types";
import { isNotNullOrUndefined as isNotNullOrUndefined9, isNullOrUndefined as isNullOrUndefined7, raise as raise3, raiseUnreachable as raiseUnreachable6, vySerialize } from "@vy/utils";
function getFixedChargePriceForAllIntervals(pricing, numOfIntervals) {
  const price = getFixedChargePrice(pricing);
  switch (pricing.pricingModel) {
    case PRICING_MODELS5.PercentageDeduction:
    case PRICING_MODELS5.Commission:
      return 0;
    case PRICING_MODELS5.FixedOneTime:
    case PRICING_MODELS5.Refund:
      return price;
    case PRICING_MODELS5.Subscription:
      return isNotNullOrUndefined9(pricing.priceConfiguration.seats) ? price * pricing.priceConfiguration.seats * numOfIntervals : price * numOfIntervals;
    case PRICING_MODELS5.CommercialTerms:
      return price;
    case PRICING_MODELS5.FixedDeduction:
      return price;
    default:
      return raiseUnreachable6(pricing);
  }
}
function getFixedChargePrice(pricing) {
  if (pricing.pricingModel === PRICING_MODELS5.Commission || pricing.pricingModel === PRICING_MODELS5.PercentageDeduction) {
    return 0;
  }
  if (pricing.pricingModel === PRICING_MODELS5.FixedDeduction) {
    return -pricing.priceConfiguration.amountToDeduct;
  }
  const price = pricing.priceConfiguration?.price;
  if (isNullOrUndefined7(price)) {
    return raise3(`Price is undefined for fixed pricing: ${vySerialize(pricing, null, 2)}`);
  }
  return price;
}

// src/contracts/renew-contract.ts
import { CONTRACT_STATUSES } from "@vy/types";
import { vyDate as vyDate9 } from "@vy/utils";
function buildManualRenewContract(planId, customerId, syncStatus, startRenewalDate, contractType) {
  const newStartDate = vyDate9(startRenewalDate).startOf("day").toDate();
  return buildContractWithoutPlan(planId, customerId, syncStatus, newStartDate, contractType);
}
function buildContractWithoutPlan(planId, customerId, syncStatus, startRenewalDate, contractType) {
  return {
    planId,
    startDate: startRenewalDate,
    status: CONTRACT_STATUSES.Active,
    customerId,
    syncStatus,
    contractType
  };
}

// src/crossing-measurement/index.ts
import { getMapValues, groupBy, isNullOrUndefined as isNullOrUndefined8, sort, sumBy as sumBy5, validateNotNullOrUndefined as validateNotNullOrUndefined3 } from "@vy/utils";
function findRequiredLimitCrossingMeasurement(measurements, limit, initialValue) {
  const crossingMeasurement = findLimitCrossingMeasurement(measurements, limit, initialValue);
  if (!crossingMeasurement) {
    throw new Error(`No measurement found that crosses limit ${limit}`);
  }
  return crossingMeasurement;
}
function findLimitCrossingMeasurement(measurements, limit, initialValue, sortByOrder = false) {
  let currentValue = initialValue;
  const sortedMeasurements = sortByOrder ? measurements : sortMeasurements(measurements);
  const measurementsByStartTime = groupBy(
    sortedMeasurements,
    (measurement) => sortByOrder ? measurement.id : measurement.period.startTime.getTime()
  );
  for (const measurementsGroup of getMapValues(measurementsByStartTime)) {
    const measurementGroupValue = sumBy5(measurementsGroup, (measurement) => measurement.value);
    if (currentValue + measurementGroupValue > limit) {
      return buildFindLimitCrossingMeasurementResult(measurementsGroup, currentValue, limit);
    }
    currentValue += measurementGroupValue;
  }
  return void 0;
}
function sortMeasurements(measurements) {
  return sort(measurements, (measurement) => measurement.period.startTime);
}
function buildFindLimitCrossingMeasurementResult(measurementGroup, groupCrossedFrom, limit) {
  const { preCrossingMeasurements, postCrossingMeasurements, crossingMeasurement } = getCommitmentCrossingGroups(measurementGroup, groupCrossedFrom, limit);
  validateNotNullOrUndefined3(crossingMeasurement, "crossingMeasurement");
  const crossedFrom = sumBy5(preCrossingMeasurements, (measurement) => measurement.value) + groupCrossedFrom;
  const crossedTo = crossedFrom + crossingMeasurement.value;
  return {
    measurement: crossingMeasurement,
    relatedPreCrossingMeasurements: preCrossingMeasurements,
    relatedPostCrossingMeasurements: postCrossingMeasurements,
    crossedFrom,
    limit,
    crossedTo
  };
}
function getCommitmentCrossingGroups(measurementGroup, crossedFrom, limit) {
  return measurementGroup.reduce(
    (crossingMeasurementsAccumulator, currentMeasurement) => {
      crossingMeasurementsAccumulator.currentValue += currentMeasurement.value;
      if (crossingMeasurementsAccumulator.currentValue > limit) {
        return isNullOrUndefined8(crossingMeasurementsAccumulator.crossingMeasurement) ? {
          ...crossingMeasurementsAccumulator,
          crossingMeasurement: currentMeasurement
        } : {
          ...crossingMeasurementsAccumulator,
          postCrossingMeasurements: [
            ...crossingMeasurementsAccumulator.postCrossingMeasurements,
            currentMeasurement
          ]
        };
      }
      return {
        ...crossingMeasurementsAccumulator,
        preCrossingMeasurements: [
          ...crossingMeasurementsAccumulator.preCrossingMeasurements,
          currentMeasurement
        ]
      };
    },
    {
      preCrossingMeasurements: [],
      postCrossingMeasurements: [],
      crossingMeasurement: void 0,
      currentValue: crossedFrom
    }
  );
}

// src/currency/build-currency-level.ts
import { DEFAULT_CURRENCY } from "@vy/types";
function buildCurrencyLevel(entity) {
  return entity.invoiceCurrency ?? entity.contractCurrency ?? entity.customerCurrency ?? DEFAULT_CURRENCY;
}

// src/currency-conversion/convert-currency-to-usd.ts
import { validateNotNullOrUndefined as validateNotNullOrUndefined4 } from "@vy/utils";
function convertCurrencyToUsd(amount, baseCurrency, currencyExchangeRates) {
  validateCurrencyIsSupported(baseCurrency, currencyExchangeRates);
  const exchangeRate = currencyExchangeRates[baseCurrency];
  validateNotNullOrUndefined4(exchangeRate, `Missing exchange rate for currency: ${baseCurrency}`);
  return amount / exchangeRate;
}
function validateCurrencyIsSupported(currency, currencyExchangeRates) {
  if (currency in currencyExchangeRates === false) {
    throw new Error(`Unsupported currency: ${currency}`);
  }
}

// src/currency-conversion/format-currency.ts
import { CURRENCIES } from "@vy/types";
import { raiseUnreachable as raiseUnreachable7 } from "@vy/utils";
function getCurrencySymbol(currency) {
  switch (currency) {
    case CURRENCIES.Eur:
      return "\u20AC";
    case CURRENCIES.Gbp:
      return "\xA3";
    case CURRENCIES.Ils:
      return "\u20AA";
    case void 0:
    case CURRENCIES.Usd:
      return "$";
    case CURRENCIES.Cad:
      return "C$";
    case CURRENCIES.Aud:
      return "A$";
    case CURRENCIES.Cop:
      return "COL$";
    case CURRENCIES.Brl:
      return "R$";
    case CURRENCIES.Ars:
      return "AR$";
    case CURRENCIES.Inr:
      return "\u20B9";
    case CURRENCIES.Mxn:
      return "MX$";
    default:
      return raiseUnreachable7(currency);
  }
}

// src/current-billing-cycle-calculator/index.ts
import { vyDate as vyDate10 } from "@vy/utils";
function getCurrentInvoiceBillingCycle(contractStartDate, billingIntervalInMoths) {
  const contractStartOfMonth = contractStartDate.startOf("month");
  const monthsFromStartOfContract = vyDate10().startOf("month").diff(contractStartOfMonth, "month");
  const amountOfPastBillingCycles = Math.floor(monthsFromStartOfContract / billingIntervalInMoths);
  const lastDayInBillingCycle = contractStartOfMonth.add(monthsFromStartOfContract, "month").add(billingIntervalInMoths - 1, "month").endOf("month");
  const startTime = contractStartOfMonth.add(amountOfPastBillingCycles, "month").startOf("month");
  return {
    startTime: startTime.toDate(),
    endTime: lastDayInBillingCycle.toDate()
  };
}

// src/custom-fields/field-mapping-by-entity.ts
function getFieldMappingByEntity(entityMappings, entity) {
  return entityMappings.find((entityMapping) => entityMapping.entity === entity)?.fields;
}

// src/custom-fields/custom-fields-parser.ts
import { isDate, isNumber, isString, vyDate as vyDate11 } from "@vy/utils";
function parseCustomFields(rawData, customFields) {
  return customFields.map((mapping) => {
    const value = getValueFromAccessor(rawData, mapping.accessor);
    return {
      internalKey: mapping.name,
      externalKey: mapping.accessor.join("."),
      value
    };
  });
}
function getValueFromAccessor(obj, accessor) {
  const value = accessor.reduce(
    (acc, key) => acc && typeof acc === "object" ? acc[key] : void 0,
    obj
  );
  return formatValue(value);
}
function formatValue(value) {
  if (isDate(value)) {
    return vyDate11(value).format("MM/DD/YYYY");
  }
  if (isString(value) || isNumber(value)) {
    return value;
  }
  return null;
}

// src/discounts/discount-models.ts
import { DISCOUNT_MODELS } from "@vy/types";
function isFixedDiscount(type) {
  return type === DISCOUNT_MODELS.FIXED;
}
function isPercentageDiscount(type) {
  return type === DISCOUNT_MODELS.PERCENTAGE;
}

// src/discounts/discount-calculator.ts
import { isNotNullOrUndefined as isNotNullOrUndefined10, safeDivision } from "@vy/utils";

// src/discounts/get-discount-configuration.ts
function getDiscountConfiguration(product) {
  if (isOneTimeProduct(product) || isSubscriptionProduct(product) || isCommercialTermsProduct(product)) {
    return product.variants[0].pricing.priceConfiguration?.discountConfiguration;
  }
  return void 0;
}
function getPricingDiscountConfiguration(pricing) {
  if (isCommercialTermsPricing(pricing) || isOneTimePricing(pricing) || isSubscriptionPricing(pricing) || isTieredSubscriptionPricing(pricing)) {
    return pricing.priceConfiguration?.discountConfiguration;
  }
  return void 0;
}

// src/discounts/discount-calculator.ts
function calculateDiscountedPrice(estimatedPrice, type, amountToDeduct, seats) {
  if (isFixedDiscount(type)) {
    return calculateFixedDiscount(estimatedPrice, amountToDeduct, seats);
  }
  if (isPercentageDiscount(type)) {
    return calculatePercentageDiscount(estimatedPrice, amountToDeduct);
  }
  return estimatedPrice;
}
function getLineItemDiscountByPricing(pricing, estimatedPrice, exchangeRate = 1) {
  const priceConfiguration = getPricingDiscountConfiguration(pricing);
  const discount = isNotNullOrUndefined10(priceConfiguration) ? getDiscountAmount(
    priceConfiguration.type,
    priceConfiguration.amountToDeduct,
    estimatedPrice,
    exchangeRate
  ) : 0;
  const installments = "installments" in pricing.priceConfiguration ? pricing.priceConfiguration.installments ?? 1 : 1;
  return safeDivision(discount, installments);
}
function getDiscountAmount(type, amountToDeduct, estimatedPrice, exchangeRate) {
  if (isFixedDiscount(type)) {
    return calculateFixedDiscountAmount(amountToDeduct, exchangeRate);
  }
  if (isPercentageDiscount(type)) {
    return calculatePercentageAmount(estimatedPrice, amountToDeduct);
  }
  return 0;
}
function calculateFixedDiscountAmount(amountToDeduct, exchangeRate = 1) {
  return Math.abs(amountToDeduct) * exchangeRate;
}
function calculatePercentageAmount(estimatedPrice, amountToDeduct) {
  return estimatedPrice * (amountToDeduct / 100);
}
function calculateFixedDiscount(estimatedPrice, amountToDeduct, seats) {
  const discountAmount = calculateFixedDiscountAmount(amountToDeduct);
  return estimatedPrice - discountAmount * seats;
}
function calculatePercentageDiscount(estimatedPrice, amountToDeduct) {
  const discountAmount = calculatePercentageAmount(estimatedPrice, amountToDeduct);
  return estimatedPrice - discountAmount;
}

// src/discounts/get-formatted-discount.ts
import { isNullOrUndefined as isNullOrUndefined9 } from "@vy/utils";

// src/price.ts
var FORMAT_PRICE_MAXIMUM_FRACTION_DIGITS = 2;
var FORMAT_PRICE_MINIMUM_FRACTION_DIGITS = 2;
var PRICE_ZERO_THRESHOLD = 1e-6;
var UNIT_PRICE_SIGNIFICANT_DIGITS = 2;
function formatPrice(price, currency, maximumFractionDigits = FORMAT_PRICE_MAXIMUM_FRACTION_DIGITS, minimumFractionDigits = FORMAT_PRICE_MINIMUM_FRACTION_DIGITS, showCurrencySymbol = true) {
  const isOutOfThreshold = Math.abs(price) < PRICE_ZERO_THRESHOLD;
  const priceToProcess = isOutOfThreshold ? 0 : price;
  const { integerPart, paddedFractionalPart } = splitPriceIntoParts(priceToProcess, maximumFractionDigits);
  const formattedPrice = `${integerPart}.${paddedFractionalPart}`;
  const formattedPriceWithCurrency = createFormattedPriceWithCurrency(
    formattedPrice,
    currency,
    maximumFractionDigits,
    minimumFractionDigits,
    showCurrencySymbol
  );
  const finalFormattedPrice = omitPotentialNegativeSign(formattedPriceWithCurrency);
  return finalFormattedPrice;
}
function formatUnitPrice(price, currency, showCurrencySymbol = true) {
  const maximumFractionDigits = getUnitPriceMaximumFractionDigits(price);
  return formatPrice(price, currency, maximumFractionDigits, FORMAT_PRICE_MINIMUM_FRACTION_DIGITS, showCurrencySymbol);
}
function getUnitPriceMaximumFractionDigits(price) {
  if (Number.isInteger(price)) {
    return FORMAT_PRICE_MAXIMUM_FRACTION_DIGITS;
  }
  const fractionalPart = Math.abs(price).toString().split(".")[1] ?? "";
  const leadingZeroesCount = Array.from(fractionalPart).findIndex((digit) => digit !== "0");
  const safeLeadingZeroesCount = leadingZeroesCount === -1 ? 0 : leadingZeroesCount;
  return Math.max(FORMAT_PRICE_MAXIMUM_FRACTION_DIGITS, safeLeadingZeroesCount + UNIT_PRICE_SIGNIFICANT_DIGITS);
}
function splitPriceIntoParts(price, maximumFractionDigits) {
  const [integerPart, fractionalPart = ""] = price.toString().split(".");
  const paddedFractionalPart = fractionalPart.padEnd(maximumFractionDigits, "0").slice(0, maximumFractionDigits);
  return { integerPart, paddedFractionalPart };
}
function createFormattedPriceWithCurrency(price, currency, maximumFractionDigits = FORMAT_PRICE_MAXIMUM_FRACTION_DIGITS, minimumFractionDigits = FORMAT_PRICE_MINIMUM_FRACTION_DIGITS, showCurrencySymbol = true) {
  return new Number(price).toLocaleString("en-US", {
    ...showCurrencySymbol && { style: "currency", currencyDisplay: "symbol", currency },
    maximumFractionDigits,
    minimumFractionDigits
  });
}
function omitPotentialNegativeSign(formattedPrice) {
  const negativeSign = "-";
  const isNegative3 = formattedPrice.includes(negativeSign);
  const priceWithoutNegativeSign = isNegative3 ? formattedPrice.replace(negativeSign, "") : formattedPrice;
  return `${isNegative3 ? `(${priceWithoutNegativeSign})` : priceWithoutNegativeSign}`;
}
function formatRevSharePrice(price) {
  return `${price} %`;
}

// src/discounts/get-formatted-discount.ts
function getFormattedDiscount(lineItem, currency) {
  const discountConfiguration = getDiscountConfiguration(lineItem.lineItemProduct);
  const discount = lineItem.revenueBreakdown.discount;
  if (isNullOrUndefined9(discountConfiguration) || discount === 0) {
    return void 0;
  }
  return formatDiscount(discountConfiguration, currency);
}
function getFormattedDiscountForProduct(product, currency) {
  const discountConfiguration = getDiscountConfiguration(product);
  if (isNullOrUndefined9(discountConfiguration)) {
    return void 0;
  }
  return formatDiscount(discountConfiguration, currency);
}
function formatDiscount(discountConfiguration, currency) {
  if (isPercentageDiscount(discountConfiguration.type)) {
    return formatPercentageDiscount(discountConfiguration);
  }
  return formatCurrencyDiscount(discountConfiguration, currency);
}
function formatPercentageDiscount(discountConfiguration) {
  return `${discountConfiguration.amountToDeduct}%`;
}
function formatCurrencyDiscount(discountConfiguration, currency) {
  return formatPrice(discountConfiguration.amountToDeduct, currency);
}

// src/empty-monthly-revenue-builder.ts
function getEmptyMonthlyRevenue() {
  return {
    scheduledCharge: 0,
    revenue: 0,
    deferredRevenue: 0,
    unbilledRevenue: 0
  };
}

// src/empty-revenue-breakdown.ts
function buildEmptyRevenueBreakdown() {
  return {
    total: 0,
    subtotal: 0,
    discount: 0,
    overage: 0,
    creditsUsed: 0,
    tax: 0
  };
}

// src/filter-db-mapper.ts
import { isNullOrUndefined as isNullOrUndefined10, isNotEmptyArray as isNotEmptyArray2, purge, withDefault as withDefault3 } from "@vy/utils";
function fromDbFilter(filter) {
  const nonEmptyConditions = getNonEmptyConditions(filter?.conditions);
  return isNullOrUndefined10(nonEmptyConditions) ? void 0 : {
    conditions: nonEmptyConditions
  };
}
function getNonEmptyConditions(conditions) {
  const nonEmptyCriteria = withDefault3(conditions, []).map(
    (condition) => getNonEmptyCriteria(condition.criterions)
  );
  const purgedNonEmptyCriteria = purge(nonEmptyCriteria);
  const validConditions = purgedNonEmptyCriteria.map((criterion) => ({
    criterions: criterion
  }));
  return isNotEmptyArray2(validConditions) ? validConditions : void 0;
}
function getNonEmptyCriteria(criteria) {
  return isNotEmptyArray2(criteria) ? criteria : void 0;
}

// src/filter-period-measurements.ts
import { isDateBetween as isDateBetween3 } from "@vy/utils";
function filterPeriodMeasurements(measurements, period) {
  return measurements.filter((measurement) => isDateBetween3(measurement.period.startTime, period));
}

// src/get-commitment-crossing-measurement.ts
import { validateNotNullOrUndefined as validateNotNullOrUndefined5, flatExtract, getRequiredMapValue, uniqueBy as uniqueBy2 } from "@vy/utils";
function getCrossingCommitmentMeasurement(commitmentConsumptionGroup) {
  validateNotNullOrUndefined5(commitmentConsumptionGroup.lineItemProducts[0].commitment, "commitment");
  const limit = commitmentConsumptionGroup.lineItemProducts[0].commitment.amount;
  const variants = flatExtract(commitmentConsumptionGroup.lineItemProducts, "variants");
  const commitmentConsumingMeasurements = variants.flatMap(
    (variant) => getRequiredMapValue(
      commitmentConsumptionGroup.ytdMeasurementsByProductVariantId,
      variant.productVariantId
    )
  );
  const uniqueIdMeasurements = uniqueBy2(commitmentConsumingMeasurements, "id");
  return findLimitCrossingMeasurement(uniqueIdMeasurements, limit, 0);
}

// src/get-invoice-days.ts
import { PAYMENT_TERMS as PAYMENT_TERMS2 } from "@vy/types";
import { vyDate as vyDate12 } from "@vy/utils";
function getIssueDay(paymentTerm, billingCycle) {
  return paymentTerm === PAYMENT_TERMS2.Postpayment ? vyDate12(billingCycle.endTime) : vyDate12(billingCycle.startTime);
}
function getPaymentDueDay(paymentTerm, billingCycle, paymentDueDays) {
  const issueDay = getIssueDay(paymentTerm, billingCycle);
  return issueDay.add(paymentDueDays, "day");
}

// src/get-invoice-email-recipients.ts
import {
  extract as extract2,
  isNotEmptyArray as isNotEmptyArray3,
  isNotEmptyString,
  isNotNullOrUndefined as isNotNullOrUndefined11,
  isSingleItem as isSingleItem2,
  purge as purge2
} from "@vy/utils";
function getInvoiceEmailRecipients(contacts) {
  const recipientsWithMailAddress = contacts.filter((contact) => {
    const email = contact.email ?? "";
    const receiveInvoiceEmail = contact.receiveInvoiceEmail ?? false;
    const shouldSendInvoiceEmail = receiveInvoiceEmail && isNotEmptyString(email.trim());
    return shouldSendInvoiceEmail;
  });
  const defaultRecipient = isSingleItem2(recipientsWithMailAddress) ? recipientsWithMailAddress.at(0) : void 0;
  const recipientsWithSendInvoicesEnabled = recipientsWithMailAddress.filter(
    (contact) => contact.receiveInvoiceEmail
  );
  if (isNotEmptyArray3(recipientsWithSendInvoicesEnabled)) {
    const emails = extract2(recipientsWithSendInvoicesEnabled, "email");
    return purge2(emails);
  }
  return isNotNullOrUndefined11(defaultRecipient?.email) ? [defaultRecipient.email] : [];
}

// src/get-tier-usage-price.ts
import { DISCOUNT_TYPES as DISCOUNT_TYPES2 } from "@vy/types";
import { isNullOrUndefined as isNullOrUndefined11 } from "@vy/utils";
function getTierUsagePrice(tierUsageData, initialPrice) {
  if (isNullOrUndefined11(tierUsageData.discountConfiguration)) {
    return initialPrice;
  }
  const { amount, type } = tierUsageData.discountConfiguration;
  return type === DISCOUNT_TYPES2.Fixed ? amount : initialPrice - amount * initialPrice / 100;
}

// src/group-measurements-by-billing-cycle.ts
import { getMapValueOrDefault, isDateBetween as isDateBetween4, isNullOrUndefined as isNullOrUndefined12, periodToString } from "@vy/utils";
function groupMeasurementsByBillingCycle(measurements, billingPeriod, billingInterval, contractStartDate, isCalendarAligned) {
  const billingCycles = calculateBillingCyclesInPeriod(
    billingPeriod,
    billingInterval,
    contractStartDate,
    isCalendarAligned
  );
  return measurements.reduce((measurementsByPeriod, measurement) => {
    const period = billingCycles.find(
      (billingCycle) => isDateBetween4(measurement.period.startTime, billingCycle)
    );
    if (isNullOrUndefined12(period)) {
      return measurementsByPeriod;
    }
    const periodString = periodToString(period);
    const existingValues = getMapValueOrDefault(measurementsByPeriod, periodString, []);
    return measurementsByPeriod.set(periodString, [...existingValues, measurement]);
  }, /* @__PURE__ */ new Map());
}

// src/hash-with-timestamp.ts
import crypto from "crypto";
function hashWithTimestamp(payload, timestamp) {
  const body = JSON.stringify(payload);
  const message = `${timestamp}.${body}`;
  return crypto.createHash("sha256").update(message).digest();
}

// src/inner-periods-calculator/inner-periods-calculator-service.ts
import {
  extract as extract3,
  getEndOfPreviousDay,
  getMaxDate,
  getMinDate,
  mapPeriodToVyDatePeriod as mapPeriodToVyDatePeriod2,
  purge as purge3,
  safeDivision as safeDivision2,
  validateNotEmptyArray as validateNotEmptyArray2,
  vyDate as vyDate13
} from "@vy/utils";
function calculateInnerPeriods(period, interval, isCalendarAligned) {
  const yearlyAlignedPeriod = getYearlyAlignedInnerPeriod(period);
  const defaultInnerPeriods = getPeriodsBetweenByInterval(period, interval);
  const yearlyAlignedInnerPeriods = getPeriodsBetweenByInterval(yearlyAlignedPeriod, interval);
  validateNotEmptyArray2(defaultInnerPeriods);
  validateNotEmptyArray2(yearlyAlignedInnerPeriods);
  return isCalendarAligned ? getCalendarAlignedInnerPeriods(defaultInnerPeriods, yearlyAlignedInnerPeriods) : buildPeriodsWithLimit(defaultInnerPeriods, period.startTime, period.endTime);
}
function getYearlyAlignedInnerPeriod(period) {
  return {
    startTime: vyDate13(period.startTime).startOf("year").toDate(),
    endTime: vyDate13(period.endTime).endOf("year").toDate()
  };
}
function getProductAlignedInnerPeriod(period) {
  return {
    startTime: vyDate13(period.startTime).subtract(1, "year").toDate(),
    endTime: vyDate13(period.endTime).add(1, "year").toDate()
  };
}
function getCalendarAlignedInnerPeriods(innerPeriods, yearlyAlignedInnerPeriods) {
  const innerPeriodStartDates = extract3(innerPeriods, "startTime");
  const minStartDate = getMinDate(...innerPeriodStartDates);
  const innerPeriodEndDates = extract3(innerPeriods, "endTime");
  const maxEndDate = getMaxDate(...innerPeriodEndDates);
  return buildPeriodsWithLimit(yearlyAlignedInnerPeriods, minStartDate, maxEndDate);
}
function buildPeriodsWithLimit(periods, minDate, maxDate) {
  const nullablePeriods = periods.map((period) => {
    const startTime = getMaxDate(period.startTime, minDate);
    const endTime = getMinDate(period.endTime, maxDate);
    if (endTime.getTime() < startTime.getTime() || startTime.getTime() > endTime.getTime()) {
      return void 0;
    }
    return {
      startTime,
      endTime
    };
  });
  return purge3(nullablePeriods);
}
function getPeriodsBetweenByInterval(period, interval) {
  const vyDatePeriod = mapPeriodToVyDatePeriod2(period);
  const factionMonthsBetween = vyDatePeriod.endTime.diff(period.startTime, "months", true);
  const monthsBetween = Math.ceil(factionMonthsBetween);
  const periodsAmountToCreate = safeDivision2(monthsBetween, interval);
  if (periodsAmountToCreate <= 0) {
    return [period];
  }
  const periods = [];
  for (let offset = 0, periodCount = 0; periodCount < periodsAmountToCreate; periodCount++, offset += interval) {
    const nextPeriodStart = vyDatePeriod.startTime.add(offset, "month");
    const nextPeriodEndDate = vyDatePeriod.startTime.add(offset + interval, "month");
    const endOfPerviousDay = getEndOfPreviousDay(nextPeriodEndDate);
    const thisPeriodEnd = getMinDate(endOfPerviousDay, period.endTime);
    periods.push({
      startTime: nextPeriodStart.toDate(),
      endTime: thisPeriodEnd
    });
  }
  return periods;
}

// src/integration-entities/find-integration-system-in-integration-entities/find-erp-entity-in-integration-entities.ts
import { ERP_INTEGRATION_PROVIDERS } from "@vy/types";
import { values } from "@vy/utils";
function findErpEntityInIntegrationEntities(integrationEntities) {
  const integrationProvidersValues = values(ERP_INTEGRATION_PROVIDERS);
  return integrationEntities.find(
    (entity) => integrationProvidersValues.includes(entity.integrationProvider)
  );
}

// src/integration-entities/find-integration-system-in-integration-entities/find-crm-entity-in-integration-entities.ts
import { CRM_INTEGRATION_PROVIDERS } from "@vy/types";
import { values as values2 } from "@vy/utils";
function findCrmEntityInIntegrationEntities(integrationEntities) {
  const integrationProvidersValues = values2(CRM_INTEGRATION_PROVIDERS);
  return integrationEntities.find(
    (entity) => integrationProvidersValues.includes(entity.integrationProvider)
  );
}

// src/integration-entities/find-integration-system-in-integration-entities/find-payment-entity-in-integration-entities.ts
import { PAYMENT_INTEGRATION_PROVIDERS } from "@vy/types";
import { values as values3 } from "@vy/utils";
function findPaymentEntityInIntegrationEntities(integrationEntities) {
  const integrationProvidersValues = values3(PAYMENT_INTEGRATION_PROVIDERS);
  return integrationEntities.find(
    (entity) => integrationProvidersValues.includes(entity.integrationProvider)
  );
}
function findManyPaymentEntityInIntegrationEntities(integrationEntities) {
  const integrationProvidersValues = values3(PAYMENT_INTEGRATION_PROVIDERS);
  return integrationEntities.filter(
    (entity) => integrationProvidersValues.includes(entity.integrationProvider)
  );
}

// src/integration-entities/find-integration-system-in-integration-entities/find-integration-entity-in-integration-entities.ts
import { isNullOrUndefined as isNullOrUndefined13, raise as raise4 } from "@vy/utils";
function findIntegrationByProviderInEntities(integrationEntities, integrationProvider) {
  return integrationEntities.find(
    (integrationEntity) => integrationEntity.integrationProvider === integrationProvider
  );
}
function findRequiredIntegrationByProviderInEntities(integrationEntities, integrationProvider) {
  const integrationEntity = findIntegrationByProviderInEntities(integrationEntities, integrationProvider);
  if (isNullOrUndefined13(integrationEntity)) {
    raise4(`Integration entity not found for provider ${integrationProvider}`);
  }
  return integrationEntity;
}

// src/integration-entities/build-integration-entity.ts
import { SYNC_STATUSES } from "@vy/types";
import { withDefault as withDefault4 } from "@vy/utils";
import { now as now3 } from "@vy/utils";
function buildBaseIntegrationEntity(integrationProvider, integrationExternalId, syncStatus, updatedAt) {
  return {
    updatedAt: withDefault4(updatedAt, now3()),
    syncStatus: withDefault4(syncStatus, SYNC_STATUSES.Synced),
    integrationProvider,
    integrationExternalId
  };
}
function buildErpIntegrationEntity(integrationProvider, integrationExternalId, syncStatus, updatedAt) {
  const baseIntegrationEntity = buildBaseIntegrationEntity(
    integrationProvider,
    integrationExternalId,
    syncStatus,
    updatedAt
  );
  return {
    ...baseIntegrationEntity,
    integrationProvider
  };
}

// src/integration-entities/build-anrok-integration-entity.ts
import { INTEGRATION_PROVIDERS, SYNC_STATUSES as SYNC_STATUSES2 } from "@vy/types";
import { now as now4 } from "@vy/utils";
function buildAnrokIntegrationEntityWithMetadata(productId) {
  const integrationEntity = buildBaseIntegrationEntity(
    INTEGRATION_PROVIDERS.anrok,
    productId,
    SYNC_STATUSES2.Synced,
    now4()
  );
  return {
    ...integrationEntity,
    integrationProvider: INTEGRATION_PROVIDERS.anrok,
    metadata: {
      productId
    }
  };
}

// src/integration-entities/merge-integration-entities-with-fallback.ts
function mergeIntegrationEntitiesWithFallback(primaryIntegrationEntities, fallbackIntegrationEntities) {
  const primaryEntities = primaryIntegrationEntities ?? [];
  const fallbackEntities = fallbackIntegrationEntities ?? [];
  const providersInPrimary = primaryEntities.map(({ integrationProvider }) => integrationProvider);
  const uniqueProvidersInPrimary = new Set(providersInPrimary);
  const fallbackOnly = fallbackEntities.filter(
    (entity) => !uniqueProvidersInPrimary.has(entity.integrationProvider)
  );
  return [...primaryEntities, ...fallbackOnly];
}

// src/integration-entities/stripe-payment-link-getter.ts
import { INTEGRATION_PROVIDERS as INTEGRATION_PROVIDERS2, PaymentProviderIntegrationEntitySchema } from "@vy/types";
import { withDefault as withDefault5 } from "@vy/utils";
function getStripePaymentLink(integrationEntities) {
  const stripeIntegration = findIntegrationByProviderInEntities(
    integrationEntities,
    INTEGRATION_PROVIDERS2.stripe
  );
  const { data: parsedPaymentEntity } = PaymentProviderIntegrationEntitySchema.safeParse(stripeIntegration);
  return withDefault5(parsedPaymentEntity?.metadata.link, "");
}

// src/integrations/get-erp-display-id.ts
import {
  ErpIntermediateMetadataSchema,
  ErpProviderIntegrationEntitySchema
} from "@vy/types";
import { fromNullToUndefined } from "@vy/utils";
function getErpDisplayId(erpIntegration) {
  const pareErpIntermediateMetadataParseSuccess = ErpIntermediateMetadataSchema.safeParse(erpIntegration);
  if (pareErpIntermediateMetadataParseSuccess.success) {
    return fromNullToUndefined(pareErpIntermediateMetadataParseSuccess.data.erpId);
  }
  const pareErpProviderIntegrationEntityParseSuccess = ErpProviderIntegrationEntitySchema.safeParse(erpIntegration);
  if (pareErpProviderIntegrationEntityParseSuccess.success) {
    return fromNullToUndefined(pareErpProviderIntegrationEntityParseSuccess.data.metadata.displayId);
  }
}

// src/integrations/integration-configuration-parser.ts
import { IntegrationSettingsSchema } from "@vy/types";
import { isObject, raise as raise5 } from "@vy/utils";
function arseIntegrationConfigurationFromDb(integration, schema) {
  const { integrationSettings } = integration;
  if (!isObject(integrationSettings)) {
    raise5("Integration settings is missing");
  }
  const parsedSettings = IntegrationSettingsSchema.parse(integrationSettings);
  return getParsedIntegrationConfiguration(parsedSettings, schema);
}
function getParsedIntegrationConfiguration(integrationSettings, schema) {
  const { integrationConfiguration } = integrationSettings;
  if (!isObject(integrationConfiguration)) {
    raise5("Integration configuration is missing");
  }
  return schema.parse(integrationConfiguration);
}

// src/integrations/default-integration-configuration-provider/default-configuration-provider.ts
import { INTEGRATION_PROVIDERS as INTEGRATION_PROVIDERS3 } from "@vy/types";

// src/integrations/default-integration-configuration-provider/base-default-configuration-provider.ts
function getBaseDefaultConfiguration() {
  return {};
}

// src/integrations/default-integration-configuration-provider/stripe-default-configuration-provider.ts
function getStripeDefaultConfiguration() {
  return {
    ...getBaseDefaultConfiguration(),
    autoImportCustomers: false,
    autoExportInvoices: false,
    autoExportCreatedCustomers: false,
    syncPaymentRefIdToErp: false
  };
}

// src/integrations/default-integration-configuration-provider/default-configuration-provider.ts
function getDefaultIntegrationConfiguration(provider) {
  switch (provider) {
    case INTEGRATION_PROVIDERS3.stripe:
      return getStripeDefaultConfiguration();
    default:
      return getBaseDefaultConfiguration();
  }
}

// src/invoice-id-shortener.ts
import { shortenString } from "@vy/utils";
var DEFAULT_SHORTEN_LENGTH = 5;
function IdShortener(id) {
  return shortenString(id, DEFAULT_SHORTEN_LENGTH);
}

// src/invoice-number-generator.ts
function generateInvoiceNumber(invoiceId) {
  return invoiceId.slice(-8);
}

// src/invoices/is-credit-invoice.ts
import { isNotNullOrUndefined as isNotNullOrUndefined12 } from "@vy/utils";
var isCreditLineItem = (lineItem) => {
  const product = lineItem.lineItemProduct;
  return isNotNullOrUndefined12(product) && isCreditProduct(product);
};
var isCreditInvoice = (invoice) => {
  return invoice.lineItems.every(isCreditLineItem);
};

// src/invoices/is-refund-invoice.ts
import { isNotNullOrUndefined as isNotNullOrUndefined13 } from "@vy/utils";
var isRefundLineItem = (lineItem) => {
  const product = lineItem.lineItemProduct;
  return isNotNullOrUndefined13(product) && isRefundProduct(product);
};
var isRefundInvoice = (invoice) => {
  return invoice.lineItems.every(isRefundLineItem);
};

// src/line-item/extract-line-item-id.ts
function extractLineItemId(pricedLineItemId) {
  const parts = pricedLineItemId.split("-");
  if (parts.length === 1) {
    return pricedLineItemId;
  }
  return parts.slice(0, -1).join("-");
}

// src/line-item-revenue-breakdown-builder.ts
import { DISCOUNT_MODELS as DISCOUNT_MODELS2, PRICING_MODELS as PRICING_MODELS6 } from "@vy/types";
import {
  calculatePercentageFromNumber,
  isNotNullOrUndefined as isNotNullOrUndefined14,
  safeDivision as safeDivision3,
  validateNotNullOrUndefined as validateNotNullOrUndefined6,
  vyDate as vyDate14
} from "@vy/utils";
function buildLineItemRevenueBreakdown(total, isPromotion) {
  return {
    ...buildEmptyRevenueBreakdown(),
    total,
    discount: isPromotion ? Math.abs(total) : 0
  };
}
function buildLineItemRevenueBreakdownWithDiscount(pricing, total, isPromotion, isFirstInvoice, invoicePeriod, contractStartDate, billingIntervalInMonths, exchangeRate = 1, isTierCrossPeriod = false) {
  if (isTierCrossPeriod) {
    return buildLineItemRevenueBreakdown(total, isPromotion);
  }
  if (hasRecurringDiscount(pricing)) {
    const billingCycleIndex = calculateBillingCycleIndex(
      invoicePeriod,
      contractStartDate,
      billingIntervalInMonths
    );
    const discountAmount2 = calculateRecurringDiscountAmount(pricing, total, billingCycleIndex, exchangeRate);
    return {
      ...buildEmptyRevenueBreakdown(),
      total: total - discountAmount2,
      subtotal: total,
      discount: calculateDiscountForRevenueBreakdown(isPromotion, total, discountAmount2)
    };
  }
  const shouldCalculateDiscount = isFirstInvoice ?? false;
  if (!shouldCalculateDiscount && isSubscriptionPricing(pricing)) {
    return buildLineItemRevenueBreakdown(total, isPromotion);
  }
  const discountAmount = getLineItemDiscountByPricing(pricing, total, exchangeRate);
  return {
    ...buildEmptyRevenueBreakdown(),
    total: total - discountAmount,
    subtotal: total,
    discount: calculateDiscountForRevenueBreakdown(isPromotion, total, discountAmount)
  };
}
function calculateDiscountForRevenueBreakdown(isPromotion, total, discountAmount) {
  return isPromotion ? Math.abs(total) : discountAmount;
}
function hasRecurringDiscount(pricing) {
  return (isSubscriptionPricing(pricing) || isTieredSubscriptionPricing(pricing)) && isNotNullOrUndefined14(getPricingDiscountConfiguration(pricing));
}
function calculateBillingCycleIndex(invoicePeriod, contractStartDate, billingIntervalInMonths) {
  if (!invoicePeriod || !contractStartDate || !billingIntervalInMonths) {
    return 0;
  }
  const contractStart = vyDate14(contractStartDate);
  const invoiceStart = vyDate14(invoicePeriod.startTime);
  const monthsDiff = invoiceStart.diff(contractStart, "month");
  return Math.floor(monthsDiff / billingIntervalInMonths);
}
function calculateRecurringDiscountAmount(pricing, basePrice, billingCycleIndex, exchangeRate = 1) {
  const discountConfiguration = getPricingDiscountConfiguration(pricing);
  validateNotNullOrUndefined6(discountConfiguration, "discountConfiguration");
  const recurringTimes = discountConfiguration.recurring;
  if (isNotNullOrUndefined14(recurringTimes) && billingCycleIndex >= recurringTimes) {
    return 0;
  }
  const seats = pricing.pricingModel === PRICING_MODELS6.Subscription ? pricing.priceConfiguration.seats ?? 1 : 1;
  const installments = "installments" in pricing.priceConfiguration ? pricing.priceConfiguration.installments ?? 1 : 1;
  const discountAmount = discountConfiguration.type === DISCOUNT_MODELS2.FIXED ? discountConfiguration.amountToDeduct * seats * exchangeRate : calculatePercentageFromNumber(discountConfiguration.amountToDeduct, basePrice);
  return safeDivision3(discountAmount, installments);
}

// src/line-items-filter/index.ts
import { LINE_ITEM_TYPES as LINE_ITEM_TYPES2 } from "@vy/types";
import { isNotNullOrUndefined as isNotNullOrUndefined16 } from "@vy/utils";

// src/line-items-filter/line-items-filter-consts.ts
import { LINE_ITEM_TYPES } from "@vy/types";
var PRODUCT_LINE_ITEM_TYPES = [
  LINE_ITEM_TYPES.Fixed,
  LINE_ITEM_TYPES.Usage,
  LINE_ITEM_TYPES.Commitment
];

// src/line-items-filter/line-items-filter-implementations/index.ts
import { isNotNullOrUndefined as isNotNullOrUndefined15 } from "@vy/utils";
function filterFixedLineItems(lineItems) {
  return lineItems.every(isFixedLineItem);
}
function filterUsageLineItems(lineItems) {
  return lineItems.every(isUsageLineItem);
}
function isFixedLineItem(lineItem) {
  return isProductLineItem(lineItem) && isFixedProduct(lineItem.lineItemProduct);
}
function isUsageLineItem(lineItem) {
  if (!isProductLineItem(lineItem)) {
    return false;
  }
  return isUsageProduct(lineItem.lineItemProduct) && !isCommitmentProduct(lineItem.lineItemProduct);
}
function isMeteredCreditLineItem(lineItem) {
  return isUsageLineItem(lineItem) && isMeteredCreditProductOnly(lineItem.lineItemProduct);
}
function isTiersUsageLineItem(lineItem) {
  return isProductLineItem(lineItem) && isTiersUsageProductOnly(lineItem.lineItemProduct) && !isCommitmentProduct(lineItem.lineItemProduct);
}
function isPerUnitLineItem(lineItem) {
  return isProductLineItem(lineItem) && isPerUnitUsageProductOnly(lineItem.lineItemProduct) && !isCommitmentProduct(lineItem.lineItemProduct);
}
function isCommitmentLineItem(lineItem) {
  return isConsumingUsageProduct(lineItem);
}
function isLineItemWithUnits(lineItem) {
  return isUsageLineItem(lineItem) || isCommitmentLineItem(lineItem);
}
function isLineItemDisabled(lineItem) {
  return lineItem.disabled ?? false;
}
function isLineItemEnabled(lineItem) {
  return !isLineItemDisabled(lineItem);
}
function validateLineItemWithUnits(lineItem) {
  if (!isLineItemWithUnits(lineItem)) {
    throw new Error("Line item is not a usage or commitment line item");
  }
}
function isConsumingUsageProduct(lineItem) {
  return isProductLineItem(lineItem) && isCommitmentProduct(lineItem.lineItemProduct);
}
function isVolumeBasedTiersLineItem(lineItem) {
  return isTiersUsageLineItem(lineItem) && isTiersVolumeProduct(lineItem.lineItemProduct);
}
function isNonRelativeFixedLineItem(lineItem) {
  return !isCommissionProduct(lineItem.lineItemProduct);
}
function isUsageOrCommitmentLineItem(lineItem) {
  return isUsageLineItem(lineItem) || isCommitmentLineItem(lineItem);
}
function isTieredLockedLineItem(lineItem) {
  if (!isUsageLineItem(lineItem)) {
    return false;
  }
  const pricingProductVariant = lineItem.lineItemProduct.variants.at(0);
  return isNotNullOrUndefined15(pricingProductVariant) && isTierLockedProductVariant(pricingProductVariant);
}

// src/line-items-filter/index.ts
function filterCommitmentLineItems(lineItems) {
  return lineItems.filter(isCommitmentLineItem);
}
function filterProductLineItems(lineItems) {
  return lineItems.filter(isProductLineItem);
}
function filterPromotionLineItems(lineItems) {
  return lineItems.filter(isPromotionLineItem);
}
function isCommissionFixedLineItem(lineItem) {
  return isCommissionProduct(lineItem.lineItemProduct);
}
function isPercentageDeductionLineItem(lineItem) {
  return isPercentageDeductionProductOnly(lineItem.lineItemProduct);
}
function isFixedDeductionLineItem(lineItem) {
  return isFixedDeductionProductOnly(lineItem.lineItemProduct);
}
function isPromotionLineItem(lineItem) {
  const isFixedLineItem2 = lineItem.type === LINE_ITEM_TYPES2.Fixed || lineItem.type === LINE_ITEM_TYPES2.NonProductFixed;
  const [variant] = lineItem.lineItemProduct.variants;
  return isFixedLineItem2 && isNotNullOrUndefined16(variant) && isPromotionProduct(lineItem.lineItemProduct);
}
function isProductLineItem(lineItem) {
  return PRODUCT_LINE_ITEM_TYPES.includes(lineItem.type);
}

// src/measurements-prices-calculators/tiers-measurements-prices-calculator.ts
function calculateTiersMeasurementsPrices(tiers, measurements) {
  let accumulatedUnits = 0;
  let accumulatedPrice = 0;
  return measurements.reduce((accumulatedMeasurements, currentMeasurement) => {
    accumulatedUnits += currentMeasurement.value;
    const currentAccumulatedPrice = calculateTiersPrice(tiers, accumulatedUnits);
    const measurementPrice = currentAccumulatedPrice - accumulatedPrice;
    accumulatedPrice = currentAccumulatedPrice;
    return [
      ...accumulatedMeasurements,
      {
        measurementId: currentMeasurement.id,
        price: measurementPrice
      }
    ];
  }, []);
}

// src/measurements-prices-calculators/per-unit-measurement-prices-calculator.ts
function calculatePerUnitMeasurementsPrices(variant, measurements) {
  const { price: pricePerUnit } = variant.pricing.priceConfiguration;
  return measurements.reduce((accumulatedMeasurements, currentMeasurement) => {
    const measurementPrice = currentMeasurement.value * pricePerUnit;
    return [
      ...accumulatedMeasurements,
      {
        measurementId: currentMeasurement.id,
        price: measurementPrice
      }
    ];
  }, []);
}

// src/measurements-sorter.ts
import { getMapValues as getMapValues2, groupBy as groupBy2, requiredAt } from "@vy/utils";
function sortMeasurementsByStartDate(measurements) {
  const measurementsByStartDate = groupBy2(
    measurements,
    (measurement) => measurement.period.startTime.getTime()
  );
  const measurementsGroups = getMapValues2(measurementsByStartDate);
  const sortedMeasurementsGroups = measurementsGroups.sort(sortMeasurementGroupsByStartDate);
  return sortedMeasurementsGroups.flatMap((measurementsGroup) => {
    const positiveMeasurements = measurementsGroup.filter((measurement) => measurement.value > 0);
    const negativeMeasurements = measurementsGroup.filter((measurement) => measurement.value <= 0);
    return [
      ...positiveMeasurements.sort(sortMeasurementsByCreatedAt),
      ...negativeMeasurements.sort(sortMeasurementsByCreatedAt)
    ];
  });
}
function sortMeasurementsByCreatedAt(currentMeasurement, nextMeasurement) {
  return currentMeasurement.createdAt.getTime() - nextMeasurement.createdAt.getTime();
}
function sortMeasurementGroupsByStartDate(currentMeasurements, nextMeasurements) {
  const firstMeasurement = requiredAt(currentMeasurements, 0);
  const secondMeasurement = requiredAt(nextMeasurements, 0);
  return firstMeasurement.period.startTime.getTime() - secondMeasurement.period.startTime.getTime();
}

// src/notifications-filter/index.ts
import { NOTIFICATION_EVENT_TYPES } from "@vy/types";
function isOverageNotification(notification) {
  return notification.eventType === NOTIFICATION_EVENT_TYPES.Overage;
}
function isAnonymousCustomerNotification(notification) {
  return notification.eventType === NOTIFICATION_EVENT_TYPES.AnonymousCustomer;
}
function isCustomerPortalLinkSentNotification(notification) {
  return notification.eventType === NOTIFICATION_EVENT_TYPES.CustomerPortalLinkSent;
}
function isInvoiceApprovedNotification(notification) {
  return notification.eventType === NOTIFICATION_EVENT_TYPES.InvoiceApproved;
}
function isUpcomingRenewalNotification(notification) {
  return notification.eventType === NOTIFICATION_EVENT_TYPES.UpcomingRenewal;
}
function isNewCustomerWithoutContractNotification(notification) {
  return notification.eventType === NOTIFICATION_EVENT_TYPES.NewCustomerWithoutContract;
}
function isUnchargedEventsNotification(notification) {
  return notification.eventType === NOTIFICATION_EVENT_TYPES.UnchargedEvents;
}
function isMonthEndReportNotification(notification) {
  return notification.eventType === NOTIFICATION_EVENT_TYPES.MonthEndReport;
}
function isTierCrossedNotification(notification) {
  return notification.eventType === NOTIFICATION_EVENT_TYPES.TierCrossed;
}
function isCommitmentCrossedNotification(notification) {
  return notification.eventType === NOTIFICATION_EVENT_TYPES.CommitmentCrossed;
}

// src/price-converter.ts
var AMOUNT_OF_SUBUNITS_IN_A_UNIT = 100;
function convertPriceFromSubunits(priceInSubUnits) {
  return priceInSubUnits / AMOUNT_OF_SUBUNITS_IN_A_UNIT;
}
function convertPriceToSubunits(priceInSubUnits) {
  return Number((priceInSubUnits * AMOUNT_OF_SUBUNITS_IN_A_UNIT).toFixed(0));
}

// src/product/product-title.ts
import { isNotEmptyString as isNotEmptyString2, isSingleItem as isSingleItem3, values as values4, whenDefined } from "@vy/utils";
function getProductTitle(lineItemProduct, activeProductConfig) {
  const productNameFromConfig = whenDefined(extractNameFromProductConfig, activeProductConfig);
  if (isNotEmptyString2(productNameFromConfig)) {
    return productNameFromConfig;
  }
  const [firstProductVariant] = lineItemProduct.variants;
  const firstProductVariantName = firstProductVariant.name;
  if (isSingleItem3(lineItemProduct.variants)) {
    return firstProductVariantName;
  }
  if (isNotEmptyString2(lineItemProduct.name)) {
    return lineItemProduct.name;
  }
  return firstProductVariantName;
}
function extractNameFromProductConfig(productConfig) {
  if (isNotEmptyString2(productConfig?.name)) {
    return productConfig.name;
  }
  const overrideVariantsValues = whenDefined(values4, productConfig?.variantPricingOverrides);
  const firstOverrideVariants = overrideVariantsValues?.at(0);
  if (isNotEmptyString2(firstOverrideVariants?.name)) {
    return firstOverrideVariants.name;
  }
}

// src/product/products-validators.ts
function validateUsageProduct(product) {
  if (!isUsageProduct(product)) {
    throw new Error("Product is not a usage product");
  }
}

// src/product-config-utils/product-config-utils.ts
import { PRODUCT_CONFIG_STATUSES } from "@vy/types";
import {
  isDateBetween as isDateBetween5,
  isNotEmptyArray as isNotEmptyArray4,
  isNotNullOrUndefined as isNotNullOrUndefined17,
  isEmptyArray as isEmptyArray2,
  getEndOfPreviousDay as getEndOfPreviousDay2,
  isNullOrUndefined as isNullOrUndefined14
} from "@vy/utils";
import { last, sortBy } from "lodash";
function sortProductConfigsByCreatedAt(configs) {
  if (isEmptyArray2(configs)) {
    return [];
  }
  const configsCopy = [...configs];
  return configsCopy.sort((config, configToCompare) => {
    const latest = getLatestProductConfigByCreatedAt([config, configToCompare]);
    return latest === config ? 1 : -1;
  });
}
function getLatestActiveProductConfig(configs, currentDate) {
  const filteredConfigs = filterProductConfigsByDateInBetweenPeriod(configs, currentDate);
  return getLatestProductConfigByCreatedAt(filteredConfigs);
}
function getLatestProductConfigByStartDate(configs, currentDate) {
  const filteredConfigs = filterProductConfigsByPeriodStartDate(configs, currentDate);
  return getLatestProductConfigByCreatedAt(filteredConfigs);
}
function getActiveProductConfigPricing(productConfigs, startDate) {
  return isNotEmptyArray4(productConfigs) && isNotNullOrUndefined17(startDate) ? getLatestActiveProductConfig(productConfigs, startDate) : void 0;
}
function getActiveProductConfigName(productConfigs, startDate) {
  return isNotEmptyArray4(productConfigs) && isNotNullOrUndefined17(startDate) ? getLatestActiveProductConfig(productConfigs, startDate)?.name : void 0;
}
function getProductConfigByStartDate(productConfigs, startDate) {
  return isNotEmptyArray4(productConfigs) && isNotNullOrUndefined17(startDate) ? getLatestProductConfigByStartDate(productConfigs, startDate) : void 0;
}
function getUniqueProductConfigPeriods(configs) {
  if (!isNotEmptyArray4(configs)) {
    return [];
  }
  const configTimePoints = configs.flatMap((config) => [
    { date: config.period.startTime, isStart: true, config },
    { date: config.period.endTime, isStart: false, config }
  ]);
  const sortedConfigTimePoints = configTimePoints.sort((a, b) => a.date.getTime() - b.date.getTime());
  const effectiveConfigs = [];
  let activeConfigs = [];
  let lastConfigDate = null;
  for (const configTimePoint of sortedConfigTimePoints) {
    if (isNotNullOrUndefined17(lastConfigDate) && activeConfigs.length > 0 && lastConfigDate.getTime() !== configTimePoint.date.getTime()) {
      const latestConfig = getLatestProductConfigByCreatedAt(activeConfigs);
      if (latestConfig) {
        effectiveConfigs.push({
          ...latestConfig,
          period: {
            startTime: getEndOfPreviousDay2(lastConfigDate),
            endTime: configTimePoint.date
          }
        });
      }
    }
    if (configTimePoint.isStart) {
      activeConfigs.push(configTimePoint.config);
    } else {
      activeConfigs = activeConfigs.filter((c) => c !== configTimePoint.config);
    }
    lastConfigDate = configTimePoint.date;
  }
  return effectiveConfigs;
}
function filterProductConfigsByDateInBetweenPeriod(configs, currentDate) {
  return configs.filter((config) => isDateBetween5(currentDate, config.period));
}
function getLatestProductConfigByCreatedAt(configs) {
  if (!isNotEmptyArray4(configs)) {
    return void 0;
  }
  const configsByCreatedAt = sortBy(configs, (config) => config.createdAt);
  const latestConfig = last(configsByCreatedAt);
  if (isNullOrUndefined14(latestConfig)) {
    return void 0;
  }
  const latestConfigCreatedAt = latestConfig.createdAt;
  const sameConfigCreatedAt = configsByCreatedAt.filter(
    (config) => config.createdAt?.getTime() === latestConfigCreatedAt?.getTime()
  );
  const revisedConfig = sameConfigCreatedAt.find((config) => isNotNullOrUndefined17(config.revisionId));
  return revisedConfig ?? latestConfig;
}
function filterProductConfigsByPeriodStartDate(configs, startDate) {
  return configs.filter((config) => {
    const { period } = config;
    return isDateBetween5(startDate, period);
  });
}
function getActiveVariants(variants, productConfigs, periodStartTime) {
  const productRevised = isNotEmptyArray4(productConfigs);
  if (!productRevised || !periodStartTime) {
    return variants;
  }
  const activeConfig = getLatestActiveProductConfig(productConfigs, periodStartTime);
  if (activeConfig?.status === PRODUCT_CONFIG_STATUSES.DELETED) {
    return [];
  }
  if (isNullOrUndefined14(activeConfig)) {
    return variants;
  }
  const activeVariants = variants.filter(
    (variant) => isNotNullOrUndefined17(activeConfig?.variantPricingOverrides?.[variant.id])
  );
  const overriddenVariants = activeVariants.map(({ id: variantId, ...restOfVariant }) => {
    const activeVariantConfig = activeConfig?.variantPricingOverrides?.[variantId];
    return {
      id: variantId,
      ...restOfVariant,
      ...activeVariantConfig
    };
  });
  return overriddenVariants;
}
function getActiveVariant(existingVariant, productConfigs, periodStartTime) {
  const currentProductConfig = getActiveProductConfigPricing(productConfigs, periodStartTime);
  const activeVariant = currentProductConfig?.variantPricingOverrides?.[existingVariant.id];
  return isNotNullOrUndefined17(activeVariant) ? {
    ...existingVariant,
    ...activeVariant
  } : existingVariant;
}

// src/product-config-utils/product-configs-start-date-sorter.ts
import { isEmptyArray as isEmptyArray3 } from "@vy/utils";
import { sortBy as sortBy2 } from "lodash";
function sortProductConfigsByStartDate(configs) {
  if (isEmptyArray3(configs)) {
    return [];
  }
  return sortBy2(configs, getStartDateSorterValue, getCreatedAtSorterValue, getPeriodLengthSorterValue);
}
function getStartDateSorterValue(config) {
  return config.period.startTime.getTime();
}
function getCreatedAtSorterValue(config) {
  return config.createdAt.getTime();
}
function getPeriodLengthSorterValue(config) {
  return config.period.endTime.getTime() - config.period.startTime.getTime();
}

// src/product-config-utils/resolve-effective-products.ts
import { PRODUCT_CONFIG_STATUSES as PRODUCT_CONFIG_STATUSES2 } from "@vy/types";
import { isNotEmptyArray as isNotEmptyArray5, isNotNullOrUndefined as isNotNullOrUndefined18 } from "@vy/utils";
var PRODUCT_CONFIG_OVERRIDE_FIELDS = [
  "basePrice",
  "billingInterval",
  "paymentTerm",
  "proRata",
  "additionalCharge",
  "chargingCadence",
  "accumulationInterval",
  "rateMultiplier"
];
function resolveEffectiveProducts(products, effectiveDate = /* @__PURE__ */ new Date()) {
  return products.reduce((resolved, product) => {
    const configs = product.productConfigs;
    if (!isNotEmptyArray5(configs)) {
      resolved.push(product);
      return resolved;
    }
    const activeConfig = getLatestActiveProductConfig(configs, effectiveDate);
    if (activeConfig?.status === PRODUCT_CONFIG_STATUSES2.DELETED) {
      return resolved;
    }
    if (!activeConfig) {
      resolved.push(product);
      return resolved;
    }
    const resolvedVariants = getActiveVariants(product.variants, configs, effectiveDate);
    const productOverrides = {};
    for (const field of PRODUCT_CONFIG_OVERRIDE_FIELDS) {
      if (isNotNullOrUndefined18(activeConfig[field])) {
        productOverrides[field] = activeConfig[field];
      }
    }
    resolved.push({
      ...product,
      ...productOverrides,
      variants: resolvedVariants
    });
    return resolved;
  }, []);
}

// src/product-estimator/index.ts
import { isNotNullOrUndefined as isNotNullOrUndefined19, raise as raise6, sumBy as sumBy6 } from "@vy/utils";

// src/product-estimator/product-estimators/fixed-product-estimator/fixed-product-estimators/apply-discount.ts
import { isNullOrUndefined as isNullOrUndefined15 } from "@vy/utils";
function applyDiscount(product, estimatedPrice) {
  const discountConfiguration = getDiscountConfiguration(product);
  if (isNullOrUndefined15(discountConfiguration)) {
    return estimatedPrice;
  }
  const seats = "seats" in product.variants[0].pricing.priceConfiguration ? product.variants[0].pricing.priceConfiguration.seats ?? 1 : 1;
  return calculateDiscountedPrice(
    estimatedPrice,
    discountConfiguration.type,
    discountConfiguration.amountToDeduct,
    seats
  );
}

// src/product-estimator/product-estimators/fixed-product-estimator/fixed-product-estimators/commercial-terms-price-estimator.ts
import { isNullOrUndefined as isNullOrUndefined16 } from "@vy/utils";
var CommercialTermsPriceEstimator = class {
  estimate(product, _calculationInput) {
    const price = product.variants.at(0)?.pricing.priceConfiguration?.price;
    if (isNullOrUndefined16(price)) {
      return 0;
    }
    return applyDiscount(product, price);
  }
};

// src/product-estimator/product-estimators/fixed-product-estimator/fixed-product-estimators/fixed-one-time-price-estimator.ts
import { isNullOrUndefined as isNullOrUndefined17 } from "@vy/utils";
var FixedOneTimePriceEstimator = class {
  estimate(product, _calculationInput) {
    const price = product.variants.at(0)?.pricing.priceConfiguration?.price;
    if (isNullOrUndefined17(price)) {
      return 0;
    }
    return applyDiscount(product, price);
  }
};

// src/product-estimator/product-estimators/fixed-product-estimator/fixed-product-estimators/subscription-price-estimator.ts
import { notDefined, whenDefined as whenDefined2 } from "@vy/null";
import { withDefault as withDefault6 } from "@vy/utils";
var SubscriptionPriceEstimator = class {
  estimate(product, billingData) {
    const basePrice = this.getBasePrice(product);
    const installments = this.getInstallments(product, billingData);
    if (installments === 0) {
      return 0;
    }
    const discountConfiguration = getDiscountConfiguration(product);
    if (notDefined(discountConfiguration)) {
      return basePrice * installments;
    }
    const discountedCycles = whenDefined2(
      discountConfiguration.recurring,
      (recurring) => Math.min(installments, recurring),
      installments
    );
    const discountedCyclePrice = applyDiscount(product, basePrice);
    const discountedCyclesTotal = discountedCyclePrice * discountedCycles;
    const fullPriceCyclesTotal = basePrice * (installments - discountedCycles);
    return discountedCyclesTotal + fullPriceCyclesTotal;
  }
  getPriceWithoutDiscount(product, billingData) {
    return this.getBasePrice(product) * this.getInstallments(product, billingData);
  }
  getBasePrice(product) {
    const { priceConfiguration } = product.variants[0].pricing;
    const seats = withDefault6(priceConfiguration?.seats, 1);
    const price = withDefault6(priceConfiguration?.price, 0);
    return price * seats;
  }
  getInstallments(product, billingData) {
    const { priceConfiguration } = product.variants[0].pricing;
    if (notDefined(billingData.billingInterval) || notDefined(billingData.duration)) {
      return 0;
    }
    const billingCyclesPerDurationInMonths = getBillingCyclesPerDurationInMonths(
      product.billingInterval,
      billingData.duration
    );
    return withDefault6(priceConfiguration?.installments, billingCyclesPerDurationInMonths);
  }
};

// src/product-estimator/product-estimators/fixed-product-estimator/fixed-product-estimators/refund-price-estimator.ts
var RefundPriceEstimator = class {
  estimate(product, _calculationInput) {
    return -Math.abs(product.variants[0].pricing.priceConfiguration.price);
  }
};

// src/product-estimator/product-estimators/usage-product-estimator/index.ts
var UsageProductPriceEstimator = class {
  estimate(product, billingInterval) {
    const billingPeriodInMonths = getBillingIntervalInMonths(billingInterval);
    return (product.basePrice ?? 0) * (12 / billingPeriodInMonths);
  }
};

// src/product-estimator/index.ts
function estimatePlan(plan) {
  const effectiveProducts = resolveEffectiveProducts(plan.products);
  const products = effectiveProducts;
  return sumBy6(
    products,
    (product) => getProductEstimation(product, plan.billingData)
  );
}
function getProductEstimation(product, billingData) {
  return isFixedProduct(product) ? getFixedProductEstimation(product, billingData) : getUsageProductEstimation(product, billingData.billingInterval);
}
function getFixedProductEstimation(product, billingData) {
  if (isOneTimeProduct(product)) {
    return new FixedOneTimePriceEstimator().estimate(product, void 0);
  }
  if (isSubscriptionProduct(product)) {
    return new SubscriptionPriceEstimator().estimate(product, billingData);
  }
  if (isCommissionProduct(product)) {
    return 0;
  }
  if (isCommercialTermsProduct(product)) {
    return new CommercialTermsPriceEstimator().estimate(product, void 0);
  }
  if (isRefundProduct(product)) {
    return new RefundPriceEstimator().estimate(product, void 0);
  }
  if (isFixedDeductionProductOnly(product)) {
    const price = -product.variants[0].pricing.priceConfiguration.amountToDeduct;
    return isNotNullOrUndefined19(price) && !isNaN(price) ? price : 0;
  }
  if (isPercentageDeductionProductOnly(product)) {
    return 0;
  }
  return raise6(`Unsupported product type ${product}`);
}
function getUsageProductEstimation(product, billingPeriod) {
  const billingPeriodInMonths = getBillingIntervalInMonths(billingPeriod);
  return (product.basePrice ?? 0) * (12 / billingPeriodInMonths);
}

// src/promotions-services/promotions-configurations-types.ts
import { PROMOTION_TYPES } from "@vy/types";
function isTrialPromotionConfiguration(promotionType, promotionConfiguration) {
  return promotionType === PROMOTION_TYPES.FreeTrial;
}
function isCreditPromotionConfiguration(promotionType, promotionConfiguration) {
  return promotionType === PROMOTION_TYPES.Credit;
}
function isPercentagePromotionConfiguration(promotionType, promotionConfiguration) {
  return promotionType === PROMOTION_TYPES.Percentage;
}
function isTrialPromotionConfigurationInput(promotionType, promotionConfiguration) {
  return promotionType === PROMOTION_TYPES.FreeTrial;
}
function isCreditPromotionConfigurationInput(promotionType, promotionConfiguration) {
  return promotionType === PROMOTION_TYPES.Credit;
}
function isPercentagePromotionConfigurationInput(promotionType, promotionConfiguration) {
  return promotionType === PROMOTION_TYPES.Percentage;
}

// src/proration-calculator/proration-calculator.ts
import {
  arePeriodsOverlapping,
  getDaysFractionBetween,
  getEndOfPreviousDay as getEndOfPreviousDay3,
  isNotNullOrUndefined as isNotNullOrUndefined20,
  isNullOrUndefined as isNullOrUndefined18,
  mapVyDatePeriodToPeriod as mapVyDatePeriodToPeriod2,
  vyDate as vyDate15,
  withNumberToPrecision
} from "@vy/utils";
var PRECISION_FOR_PRORATION_RATIO = 7;
function calculateProrationRatio(currentPeriod, fullPeriod, isProRata, isCalenderAligned = isProRata) {
  const isSameStart = vyDate15(currentPeriod.startTime).isSame(vyDate15(fullPeriod.startTime), "day");
  const isSameEnd = vyDate15(currentPeriod.endTime).isSame(vyDate15(fullPeriod.endTime), "day");
  if (!isProRata || isSameStart && isSameEnd) {
    return 1;
  }
  const vyDatePeriod = mapVyDatePeriodToPeriod2(fullPeriod);
  const fullPeriodMonths = calculateInnerPeriods(vyDatePeriod, 1, isCalenderAligned);
  const currentPeriodMonths = getCurrentPeriodMonths(currentPeriod, isCalenderAligned);
  const paddedCurrentPeriod = padCurrentPeriods(currentPeriodMonths, fullPeriodMonths);
  const baseMonthRatio = 1 / fullPeriodMonths.length;
  const preciseProrationRatio = calculateMonthlyProrationRatio(
    fullPeriodMonths,
    paddedCurrentPeriod,
    baseMonthRatio
  );
  return withNumberToPrecision(preciseProrationRatio, PRECISION_FOR_PRORATION_RATIO);
}
function padCurrentPeriods(currentPeriodMonths, fullPeriodMonths) {
  return fullPeriodMonths.map((fullPeriodMonth) => {
    const overlappingCurrentPeriod = currentPeriodMonths.find(
      (currentPeriodMonth) => arePeriodsOverlapping(currentPeriodMonth, fullPeriodMonth)
    );
    return isNotNullOrUndefined20(overlappingCurrentPeriod) ? overlappingCurrentPeriod : void 0;
  });
}
function calculateMonthlyProrationRatio(fullPeriodMonths, paddedCurrentPeriod, baseMonthRatio) {
  return fullPeriodMonths.reduce((calculatedRatio, fullMonthPeriod, index) => {
    const currentMonthPeriod = paddedCurrentPeriod.at(index);
    if (isNullOrUndefined18(currentMonthPeriod)) {
      return calculatedRatio;
    }
    const daysFractionInPeriod = getDaysFractionBetween(
      vyDate15(currentMonthPeriod.startTime),
      vyDate15(currentMonthPeriod.endTime)
    );
    const daysFractionInFullMonth = getDaysFractionBetween(
      vyDate15(fullMonthPeriod.startTime),
      vyDate15(fullMonthPeriod.endTime)
    );
    const daysInPeriod = Math.round(daysFractionInPeriod);
    const daysInFullMonth = Math.round(daysFractionInFullMonth);
    return calculatedRatio + daysInPeriod / daysInFullMonth * baseMonthRatio;
  }, 0);
}
function getCurrentPeriodMonths(period, isCalenderAligned) {
  const factionMonthsBetween = period.endTime.diff(period.startTime, "months", true);
  const monthsBetween = Math.ceil(factionMonthsBetween);
  let newStartTime = vyDate15(period.startTime);
  return Array.from({ length: monthsBetween }).map(() => {
    const endOfMonth = vyDate15(newStartTime).endOf("month");
    const endOfCalenderAlignedPeriod = endOfMonth.isBefore(period.endTime) ? endOfMonth : period.endTime;
    const followingMonthStartTime = vyDate15(newStartTime).add(1, "month");
    const endOfNonCalenderAlignedPeriod = getEndOfPreviousDay3(followingMonthStartTime);
    const endTime = isCalenderAligned ? endOfCalenderAlignedPeriod.toDate() : endOfNonCalenderAlignedPeriod;
    const newPeriod = {
      startTime: newStartTime.toDate(),
      endTime
    };
    newStartTime = vyDate15(endTime).add(1, "day").startOf("day");
    return newPeriod;
  });
}

// src/reports/bucket-file-path-builder.ts
function getReportsBucketFilePath(accountId, reportJobType) {
  return `${accountId}/${reportJobType}/${Date.now()}.json`;
}

// src/reports/reports-template-path-builder.ts
import { getRequiredEnvVar, raiseUnreachable as raiseUnreachable8 } from "@vy/utils";
import { kebabCase } from "lodash";
var USAGE_REPORT_TEMPLATE = "/UsageReportTemplate.xlsx";
function getReportDefaultTemplate(reportJobType) {
  switch (reportJobType) {
    case "CommitmentUsage":
    case "Contracts":
    case "CustomerRevenue":
    case "Customers":
    case "MonthlyClosure":
    case "ProductsUsage":
    case "UnchargedUsage":
    case "AnonymousCustomers":
    case "FailedInvoiceDeliveries":
    case "Usage":
    case "RecurringRevenue":
      return USAGE_REPORT_TEMPLATE;
    case "UnapprovedInvoices":
    case "UpcomingInvoices":
      return getReportTemplatePath(reportJobType);
    default:
      return raiseUnreachable8(reportJobType);
  }
}
function getReportTemplatePath(reportJobType) {
  const folderName = kebabCase(reportJobType);
  const fileName = getTemplateName(reportJobType);
  return `report-templates/${folderName}/${fileName}`;
}
function getTemplateName(reportJobType) {
  switch (reportJobType) {
    case "CommitmentUsage":
    case "Contracts":
    case "CustomerRevenue":
    case "Customers":
    case "MonthlyClosure":
    case "ProductsUsage":
    case "UnchargedUsage":
    case "AnonymousCustomers":
    case "FailedInvoiceDeliveries":
    case "Usage":
    case "RecurringRevenue":
      return void 0;
    case "UnapprovedInvoices":
      return getRequiredEnvVar("UNAPPROVED_INVOICES_DEFAULT_TEMPLATE");
    case "UpcomingInvoices":
      return getRequiredEnvVar("UPCOMING_INVOICES_DEFAULT_TEMPLATE");
    default:
      return raiseUnreachable8(reportJobType);
  }
}

// src/revisioned-line-items-filter/index.ts
import { REVISIONED_LINE_ITEM_TYPES } from "@vy/types";
function filterFixedRevisionedLineItems(revisionedLineItems) {
  return revisionedLineItems.filter(isFixedRevisionedLineItem);
}
function filterPromotionRevisionedLineItems(revisionedLineItems) {
  return revisionedLineItems.filter(
    (revisionedLineItem) => isFixedPromotionRevisionedLineItem(revisionedLineItem) || isPercentagePromotionRevisionedLineItem(revisionedLineItem)
  );
}
function filterUsageRevisionedLineItems(revisionedLineItems) {
  return revisionedLineItems.filter(isUsageRevisionedLineItem);
}
function isFixedRevisionedLineItem(revisionedLineItem) {
  return revisionedLineItem.type === REVISIONED_LINE_ITEM_TYPES.Fixed;
}
function isFixedPromotionRevisionedLineItem(revisionedLineItem) {
  return revisionedLineItem.type === REVISIONED_LINE_ITEM_TYPES.FixedPromotion;
}
function isPercentagePromotionRevisionedLineItem(revisionedLineItem) {
  return revisionedLineItem.type === REVISIONED_LINE_ITEM_TYPES.PercentagePromotion;
}
function isUsageRevisionedLineItem(revisionedLineItem) {
  return revisionedLineItem.type === REVISIONED_LINE_ITEM_TYPES.Usage;
}

// src/roles/get-permissions.ts
import { ROLES_PERMISSIONS, PERMISSIONS_GROUPS, MASTER_PERMISSIONS } from "@vy/types";
function getPermissionsByRoles(role) {
  const permissions = ROLES_PERMISSIONS[role] || [];
  const expandedPermissions = /* @__PURE__ */ new Set();
  for (const permission of permissions) {
    if (isMasterPermission(permission)) {
      PERMISSIONS_GROUPS[permission].forEach((subPermission) => expandedPermissions.add(subPermission));
    } else {
      expandedPermissions.add(permission);
    }
  }
  return Array.from(expandedPermissions);
}
function isMasterPermission(permission) {
  return MASTER_PERMISSIONS.includes(permission);
}

// src/sort-measurements/index.ts
function sortMeasurementsByDate(measurements) {
  return measurements.sort(
    (a, b) => new Date(a.period.startTime).getTime() - new Date(b.period.startTime).getTime()
  );
}

// src/stripe/stripe-client.ts
import Stripe from "stripe";
var STRIPE_API_VERSION = "2026-01-28.clover";
function getStripeClient(apiToken) {
  return new Stripe(apiToken, {
    apiVersion: STRIPE_API_VERSION,
    typescript: true
  });
}

// src/stripe/mappers/payment-intent-mappers.ts
import { INTEGRATION_PROVIDERS as INTEGRATION_PROVIDERS4, PAYMENT_STATUSES, SYNC_STATUSES as SYNC_STATUSES3 } from "@vy/types";
import {
  convertSecondsToMilliseconds,
  fromNullToUndefined as fromNullToUndefined2,
  isNullOrUndefined as isNullOrUndefined19,
  isString as isString2,
  raise as raise7
} from "@vy/utils";
function buildStripePaymentEntity(paymentIntent) {
  if (isNullOrUndefined19(paymentIntent.latest_charge) || isString2(paymentIntent.latest_charge)) {
    raise7("Not parsable stripe paymentIntent - latest_charge is not an ");
  }
  const latestCharge = paymentIntent.latest_charge;
  const updatedAt = new Date(convertSecondsToMilliseconds(latestCharge.created));
  const status = getPaymentStatus(paymentIntent);
  return {
    updatedAt,
    integrationProvider: INTEGRATION_PROVIDERS4.stripe,
    integrationExternalId: paymentIntent.id,
    syncStatus: SYNC_STATUSES3.Synced,
    metadata: {
      status,
      link: fromNullToUndefined2(latestCharge.receipt_url),
      transactionId: latestCharge.id
    }
  };
}
function getPaymentStatus(paymentIntent) {
  return paymentIntent.status === "succeeded" ? PAYMENT_STATUSES.Success : paymentIntent.status;
}

// src/stripe/is-unsynced-stripe-invoice.ts
import { INTEGRATION_PROVIDERS as INTEGRATION_PROVIDERS5, PaymentProviderIntegrationEntitySchema as PaymentProviderIntegrationEntitySchema2 } from "@vy/types";
import { isDate as isDate2, isKeyOf, isNotNullOrUndefined as isNotNullOrUndefined21, isNullOrUndefined as isNullOrUndefined20, isObject as isObject2, isString as isString3 } from "@vy/utils";
function isUnsyncedStripeInvoice(integrationEntities, customer) {
  const customerStripeEntity = getStripeIntegration(customer.integrationEntities ?? []);
  const stripeEntity = getStripeIntegration(integrationEntities);
  if (isNullOrUndefined20(customerStripeEntity)) {
    return false;
  }
  if (isNullOrUndefined20(stripeEntity)) {
    return true;
  }
  const mappedStripeEntity = {
    ...stripeEntity,
    updatedAt: normalizeMongoDate(stripeEntity.updatedAt)
  };
  const parsed = PaymentProviderIntegrationEntitySchema2.parse(mappedStripeEntity);
  return isNotNullOrUndefined21(customerStripeEntity) && isNullOrUndefined20(parsed.metadata?.link);
}
function getStripeIntegration(entities) {
  return findIntegrationByProviderInEntities(entities, INTEGRATION_PROVIDERS5.stripe);
}
function normalizeMongoDate(date) {
  if (isDate2(date)) {
    return date;
  }
  if (isObject2(date) && isKeyOf(date, "$date") && isString3(date.$date)) {
    return new Date(date.$date);
  }
  return date;
}

// src/system-event-mapper.ts
import { now as now5 } from "@vy/utils";
function buildNewSystemEvent(type, objectTypeId, system, timestamp, metadata) {
  return {
    type,
    objectTypeId,
    system,
    timestamp: timestamp ?? now5(),
    metadata
  };
}

// src/tiers-commitment-calculator/commitment-tiers-calculator.ts
import { isDateBetween as isDateBetween6 } from "@vy/utils";

// src/tiers-commitment-calculator/tier-commitment-info-calculator.ts
import { isNullOrUndefined as isNullOrUndefined21, purge as purge4, sumBy as sumBy7 } from "@vy/utils";
function getTierCommitmentInfo(tier, tierIndex, chunkSize, periodMeasurements, tiersFittingYtdMeasurements, firstPeriodMeasurementIndex, commitmentCrossingMeasurement, instanceKey, ignoreCommitment) {
  const firstTierMeasurement = tierIndex === 0 ? tiersFittingYtdMeasurements[0] : findTierBoundaryCrossing(tiersFittingYtdMeasurements, tier.from);
  if (isNullOrUndefined21(firstTierMeasurement)) {
    return buildCommitmentTierInfo(tierIndex, 0, 0, 0, 0, instanceKey);
  }
  const lastTierMeasurement = findTierBoundaryCrossing(tiersFittingYtdMeasurements, tier.to);
  const firstTierMeasurementIndex = tiersFittingYtdMeasurements.indexOf(firstTierMeasurement);
  const firstTierMeasurementIndexPeriodOffset = firstTierMeasurementIndex - firstPeriodMeasurementIndex;
  const lastTierMeasurementIndex = lastTierMeasurement ? tiersFittingYtdMeasurements.indexOf(lastTierMeasurement) : tiersFittingYtdMeasurements.length - 1;
  const lastTierMeasurementIndexPeriodOffset = lastTierMeasurementIndex - firstPeriodMeasurementIndex;
  const periodRelevantMeasurements = getRangeMeasurements(
    periodMeasurements,
    firstTierMeasurementIndexPeriodOffset,
    lastTierMeasurementIndexPeriodOffset
  );
  const periodUnits = sumBy7(periodRelevantMeasurements, "value");
  const ytdTierMeasurements = getRangeMeasurements(
    tiersFittingYtdMeasurements,
    firstTierMeasurementIndex,
    lastTierMeasurementIndex
  );
  const ytdUnits = sumBy7(ytdTierMeasurements, "value");
  const prePeriodMeasurements = getPrePeriodMeasurements(ytdTierMeasurements, periodRelevantMeasurements);
  const prePeriodBillableChunks = getBillableChunks(
    prePeriodMeasurements,
    commitmentCrossingMeasurement?.measurement,
    POST_COMMITMENT_FLAG,
    chunkSize,
    ignoreCommitment
  );
  const ytdBillableChunks = getBillableChunks(
    ytdTierMeasurements,
    commitmentCrossingMeasurement?.measurement,
    POST_COMMITMENT_FLAG,
    chunkSize,
    ignoreCommitment
  );
  const billableUnits = getBillableUnits(
    periodRelevantMeasurements,
    commitmentCrossingMeasurement?.measurement,
    POST_COMMITMENT_FLAG,
    ignoreCommitment
  );
  const periodChunks = ytdBillableChunks - prePeriodBillableChunks;
  const price = chunkSize > 1 ? tier.price * periodChunks : tier.price * billableUnits;
  return buildCommitmentTierInfo(tierIndex, periodUnits, ytdUnits, billableUnits, price, instanceKey);
}
function findTierBoundaryCrossing(measurements, limit) {
  let currentValue = 0;
  for (const measurement of measurements) {
    if (currentValue + measurement.value >= limit) {
      return measurement;
    }
    currentValue += measurement.value;
  }
  return void 0;
}
function getBillableChunks(measurements, commitmentCrossingMeasurement, postCommitmentFlag, chunkSize, ignoreCommitment) {
  const billableUnits = getBillableUnits(
    measurements,
    commitmentCrossingMeasurement,
    postCommitmentFlag,
    ignoreCommitment
  );
  return Math.max(Math.ceil(billableUnits / chunkSize), 0);
}
function getPrePeriodMeasurements(ytdMeasurements, periodMeasurements) {
  return ytdMeasurements.filter(
    (measurement) => periodMeasurements.every(
      (periodMeasurement) => periodMeasurement.period.startTime !== measurement.period.startTime
    )
  );
}
function buildCommitmentTierInfo(tierIndex, tierPeriodUnits, ytdUnits, tierBillableUnits, price, instanceKey) {
  const chargeInfo = buildChargeInfo(tierIndex, tierPeriodUnits, price, instanceKey);
  return {
    chargeInfo,
    commitmentContext: {
      ytdUnits,
      billableUnits: tierBillableUnits
    }
  };
}
function buildChargeInfo(tierIndex, periodUnits, price, instanceKey) {
  return {
    price,
    periodUnits,
    discountAmount: 0,
    tierContext: {
      tierIndex
    },
    instanceKey,
    discountConfiguration: null
  };
}
function getBillableUnits(tierPeriodMeasurements, commitmentCrossingMeasurement, postCommitmentFlag, ignoreCommitment) {
  if (ignoreCommitment) {
    return sumBy7(tierPeriodMeasurements, "value");
  }
  return isNullOrUndefined21(commitmentCrossingMeasurement) ? 0 : getPeriodPostCommitmentBillableUnits(
    tierPeriodMeasurements,
    commitmentCrossingMeasurement,
    postCommitmentFlag
  );
}
function getPeriodPostCommitmentBillableUnits(tierPeriodMeasurements, commitmentCrossingMeasurement, postCommitmentFlag) {
  const periodCommitmentCrossingMeasurement = tierPeriodMeasurements.find(
    (measurement) => measurement.id.includes(postCommitmentFlag)
  );
  const postCommitmentTierPeriodMeasurements = tierPeriodMeasurements.filter(
    (measurement) => measurement.period.startTime > commitmentCrossingMeasurement.period.startTime
  );
  const periodBillableMeasurements = purge4([
    periodCommitmentCrossingMeasurement,
    ...postCommitmentTierPeriodMeasurements
  ]);
  return sumBy7(periodBillableMeasurements, "value");
}
function getRangeMeasurements(measurements, enteringIndex, existingIndex) {
  const parsedEnteringIndex = Math.max(enteringIndex, 0);
  const parsedExistingIndex = Math.max(existingIndex + 1, 0);
  return measurements.slice(parsedEnteringIndex, parsedExistingIndex);
}

// src/tiers-commitment-calculator/tiers-commitment-adjusted-measurement-processors/adjusted-tier-measurement-processor.ts
import { getMapValues as getMapValues3, groupBy as groupBy3, isNotNullOrUndefined as isNotNullOrUndefined22, sumBy as sumBy8 } from "@vy/utils";
function getTiersFittingMeasurements(tiers, measurements) {
  return tiers.reduce(
    (correctedMeasurements, currentTier) => {
      const tierCrossingMeasurement = findTierCrossingMeasurement(correctedMeasurements, currentTier.to);
      return isNotNullOrUndefined22(tierCrossingMeasurement) ? getAdjustedMeasurements(correctedMeasurements, tierCrossingMeasurement) : correctedMeasurements;
    },
    [...measurements]
  );
}
function findTierCrossingMeasurement(measurements, tierLimit) {
  let currentValue = 0;
  const measurementsById = groupBy3(measurements, (measurement) => measurement.id);
  for (const measurementsGroup of getMapValues3(measurementsById)) {
    const measurementGroupValue = sumBy8(measurementsGroup, (measurement) => measurement.value);
    if (currentValue + measurementGroupValue >= tierLimit) {
      const crossingMeasurement = measurementsGroup[0];
      const crossedFrom = currentValue;
      const crossedTo = currentValue + measurementGroupValue;
      return {
        measurement: crossingMeasurement,
        crossedFrom,
        limit: tierLimit,
        crossedTo
      };
    }
    currentValue += measurementGroupValue;
  }
  return void 0;
}
function getAdjustedMeasurements(measurements, tierCrossingMeasurement) {
  const tierCrossingMeasurementIndex = measurements.indexOf(tierCrossingMeasurement.measurement);
  const adjustedMeasurements = buildAdjustedMeasurement(tierCrossingMeasurement);
  return [
    ...measurements.slice(0, tierCrossingMeasurementIndex),
    ...adjustedMeasurements,
    ...measurements.slice(tierCrossingMeasurementIndex + 1)
  ];
}
function buildAdjustedMeasurement(measurement) {
  const newPreCrossingMeasurement = {
    ...measurement.measurement,
    id: `${measurement.measurement.id}-pre`,
    value: measurement.limit - measurement.crossedFrom
  };
  const newPostCrossingMeasurement = {
    ...measurement.measurement,
    id: `${measurement.measurement.id}-post`,
    value: measurement.crossedTo - measurement.limit
  };
  return [newPreCrossingMeasurement, newPostCrossingMeasurement];
}

// src/tiers-commitment-calculator/tiers-commitment-adjusted-measurement-processors/commitment-adjusted-measurements-processor.ts
import { isNullOrUndefined as isNullOrUndefined22 } from "@vy/utils";
var NO_CROSSING_MEASUREMENT_CODE = -1;
function getCommitmentAdjustedMeasurements(measurements, postCommitmentFlag, commitmentCrossingMeasurement) {
  const clonedMeasurements = [...measurements];
  const commitmentCrossingMeasurementIndex = commitmentCrossingMeasurement && clonedMeasurements.indexOf(commitmentCrossingMeasurement.measurement);
  const notFoundCrossingMeasurement = commitmentCrossingMeasurementIndex === NO_CROSSING_MEASUREMENT_CODE || isNullOrUndefined22(commitmentCrossingMeasurementIndex) || isNullOrUndefined22(commitmentCrossingMeasurement);
  if (notFoundCrossingMeasurement) {
    return clonedMeasurements;
  }
  const [preCommitmentCrossingMeasurement, postCommitmentCrossingMeasurement] = getSplitCommitmentCrossingMeasurements(commitmentCrossingMeasurement, postCommitmentFlag);
  clonedMeasurements.splice(
    commitmentCrossingMeasurementIndex,
    1,
    preCommitmentCrossingMeasurement,
    postCommitmentCrossingMeasurement
  );
  return clonedMeasurements;
}
function getSplitCommitmentCrossingMeasurements(commitmentCrossingMeasurement, postCommitmentFlag = "") {
  const preCommitmentCrossingMeasurement = {
    ...commitmentCrossingMeasurement.measurement,
    value: commitmentCrossingMeasurement.limit - commitmentCrossingMeasurement.crossedFrom
  };
  const postCommitmentCrossingMeasurement = {
    ...commitmentCrossingMeasurement.measurement,
    id: commitmentCrossingMeasurement.measurement.id + postCommitmentFlag,
    value: commitmentCrossingMeasurement.crossedTo - commitmentCrossingMeasurement.limit
  };
  return [preCommitmentCrossingMeasurement, postCommitmentCrossingMeasurement];
}

// src/tiers-commitment-calculator/commitment-tiers-calculator.ts
var POST_COMMITMENT_FLAG = "post-commitment-crossing";
function getCommitmentTiers(tiers, chunkSize, ytdMeasurements, period, commitmentCrossingMeasurement, ignoreCommitment) {
  const commitmentAdjustedMeasurements = getCommitmentAdjustedMeasurements(
    ytdMeasurements,
    POST_COMMITMENT_FLAG,
    commitmentCrossingMeasurement
  );
  const tiersFittingYtdMeasurements = getTiersFittingMeasurements(tiers, commitmentAdjustedMeasurements);
  const periodMeasurements = getPeriodMeasurements(period, tiersFittingYtdMeasurements);
  const firstPeriodMeasurementIndex = tiersFittingYtdMeasurements.length - periodMeasurements.length;
  return tiers.map(
    (tier, tierIndex) => getTierCommitmentInfo(
      tier,
      tierIndex,
      chunkSize,
      periodMeasurements,
      tiersFittingYtdMeasurements,
      firstPeriodMeasurementIndex,
      commitmentCrossingMeasurement,
      void 0,
      ignoreCommitment
    )
  );
}
function getPeriodMeasurements(period, measurements) {
  return measurements.filter(
    (measurement) => isDateBetween6(measurement.period.startTime, period.startTime, period.endTime)
  );
}

// src/unset-product-dates-processor/unset-product-dates-processor-services/unset-product-start-date.ts
function unsetProductStartDate(product) {
  const { startDate: _startDate, ...productWithoutStartDate } = product;
  return productWithoutStartDate;
}

// src/unset-product-dates-processor/unset-product-dates-processor-services/unset-commercial-terms-product-installments.ts
function unsetCommercialTermsProductInstallments(product) {
  const modifiedVariants = product.variants.map((variant) => {
    const {
      installments: _installments,
      scheduledInstallments: _scheduledInstallments,
      ...priceConfigurationWithoutDates
    } = variant.pricing.priceConfiguration;
    return {
      ...variant,
      pricing: {
        ...variant.pricing,
        priceConfiguration: {
          ...priceConfigurationWithoutDates
        }
      }
    };
  });
  return {
    ...product,
    variants: modifiedVariants
  };
}

// src/unset-product-dates-processor/index.ts
function unsetPlanProductDates(plan) {
  const { products, ...restOfPlan } = plan;
  const productsWithoutDates = unsetProductDatesProcessor(products);
  return {
    ...restOfPlan,
    products: productsWithoutDates
  };
}
function unsetProductDatesProcessor(products) {
  const modifiedProducts = products.map((product) => {
    const productWithoutStartDate = unsetProductStartDate(product);
    if (isCommercialTermsProduct(productWithoutStartDate)) {
      return unsetCommercialTermsProductInstallments(productWithoutStartDate);
    }
    return productWithoutStartDate;
  });
  return modifiedProducts;
}

// src/usage-variant-calculator/usage-variant-calculator-factory.ts
import { raise as raise8 } from "@vy/utils";

// src/usage-variant-calculator/usage-variant-calculators/per-unit-item-calculator.ts
import { sumBy as sumBy9 } from "@vy/utils";
var PerUnitItemCalculator = class {
  calculate(variant, measurements) {
    const { price: pricePerUnit } = variant.pricing.priceConfiguration;
    const units = sumBy9(measurements, "value");
    const totalPrice = pricePerUnit * units;
    return {
      price: totalPrice,
      units,
      tiers: [
        {
          periodUnits: units,
          price: totalPrice,
          tierIndex: 0,
          discountConfiguration: null,
          chunkSize: 1
        }
      ]
    };
  }
};

// src/usage-variant-calculator/usage-variant-calculator-factory.ts
function calculateUsageProductVariant(lineItemProductVariant, measurements) {
  if (isTiersProductVariant(lineItemProductVariant)) {
    return new TiersItemCalculator().calculate(lineItemProductVariant, measurements);
  }
  if (isPerUnitProductVariant(lineItemProductVariant)) {
    return new PerUnitItemCalculator().calculate(lineItemProductVariant, measurements);
  }
  return raise8(`Unsupported pricing model: ${lineItemProductVariant.pricing.pricingModel}`);
}

// src/usage-variant-calculator/usage-variant-calculator.ts
var UsageVariantCalculator = class {
};

// src/usage-variant-charges/usage-variant-charge-comparator.ts
function compareUsageVariantCharges(usageVariantCharge1, usageVariantCharge2) {
  const isNonAnonymousUsageVariantCharge = "customerId" in usageVariantCharge1 && "customerId" in usageVariantCharge2 && "contractId" in usageVariantCharge1 && "contractId" in usageVariantCharge2;
  const isSameCustomer = !isNonAnonymousUsageVariantCharge || usageVariantCharge1.customerId === usageVariantCharge2.customerId;
  const isSameContract = !isNonAnonymousUsageVariantCharge || usageVariantCharge1.contractId === usageVariantCharge2.contractId;
  return isSameCustomer && isSameContract && usageVariantCharge1.measurementId === usageVariantCharge2.measurementId && usageVariantCharge1.productVariantId === usageVariantCharge2.productVariantId && usageVariantCharge1.period.startTime === usageVariantCharge2.period.startTime && usageVariantCharge1.period.endTime === usageVariantCharge2.period.endTime;
}

// src/usage-variant-charges-calculator/usage-variant-charges-calculator.ts
import { validateNotNullOrUndefined as validateNotNullOrUndefined7 } from "@vy/utils";

// src/usage-variant-charges-calculator/measured-prices-breakdown-calculator.ts
import {
  isBetweenInclusive as isBetweenInclusive4,
  isNegative as isNegative2,
  isNotNullOrUndefined as isNotNullOrUndefined23,
  isNullOrUndefined as isNullOrUndefined23,
  isPositive,
  requiredAt as requiredAt2,
  safeDivision as safeDivision4
} from "@vy/utils";

// src/usage-variant-charges-calculator/usage-variant-tier-builder.ts
import { PRICING_MODELS as PRICING_MODELS7 } from "@vy/types";
import { extract as extract4, isBetweenInclusive as isBetweenInclusive3, isNegative, raiseUnreachable as raiseUnreachable9 } from "@vy/utils";
import { getRequiredFirst } from "@vy/array";
function getUsageTiers(pricing, currentUnits, finalCalculationUnits, latestTierLocked) {
  validateUsagePricing(pricing);
  switch (pricing.pricingModel) {
    case PRICING_MODELS7.TieredSubscription:
      return pricing.priceConfiguration.tiers;
    case PRICING_MODELS7.Tiers:
      return buildTiersPricing(pricing, finalCalculationUnits, latestTierLocked);
    case PRICING_MODELS7.PerUnit:
      return [
        {
          from: isNegative(currentUnits) ? -Infinity : 0,
          to: Math.max(currentUnits, 0),
          price: pricing.priceConfiguration.price,
          chunkSize: pricing.priceConfiguration.chunkSize
        }
      ];
    default:
      return raiseUnreachable9(pricing);
  }
}
function buildTiersPricing(pricing, finalCalculationUnits, latestTierLocked) {
  if (isVolumePricing(pricing)) {
    return buildVolumePricing(pricing, finalCalculationUnits, latestTierLocked);
  }
  return pricing.priceConfiguration.tiers;
}
function buildVolumePricing(pricing, finalCalculationUnits, latestTierLocked) {
  const tiers = pricing.priceConfiguration.tiers;
  const toTiers = extract4(tiers, "to");
  const minTier = Math.min(...toTiers);
  const maxUnits = Math.max(minTier, finalCalculationUnits);
  const lastTier = findLastTier(pricing);
  const targetedUsageTier = tiers.find((tier) => isBetweenInclusive3(maxUnits, tier.from, tier.to));
  const usageTier = targetedUsageTier ?? lastTier;
  const usageTierIndex = tiers.indexOf(usageTier);
  const volumeIndex = Math.max(usageTierIndex, latestTierLocked ?? 0);
  const volumeTier = tiers.at(volumeIndex) ?? usageTier;
  const price = isRevSharePricing2(pricing) ? volumeTier.price / 100 : volumeTier.price;
  return [
    {
      from: 0,
      to: volumeTier.to,
      price,
      flatRate: volumeTier.flatRate,
      volumeIndex,
      chunkSize: volumeTier.chunkSize
    }
  ];
}
function findLastTier(pricing) {
  const tiers = pricing.priceConfiguration.tiers;
  const baselineTier = getRequiredFirst(tiers, "tiers");
  return tiers.reduce((currentMaxTier, currentTier) => {
    if (currentMaxTier.from > currentTier.from) {
      return currentMaxTier;
    }
    return currentTier;
  }, baselineTier);
}
function isRevSharePricing2(pricing) {
  return pricing.pricingModel === PRICING_MODELS7.Tiers && pricing.priceConfiguration.isRevShare;
}
function isVolumePricing(pricing) {
  return isTiersPricing(pricing) || isTieredSubscriptionPricing(pricing) || isRevSharePricing2(pricing) ? pricing.priceConfiguration.isVolume : false;
}

// src/usage-variant-charges-calculator/measured-prices-breakdown-calculator.ts
function getMeasuredPricesBreakdown({
  billableAmount,
  currency,
  measurement,
  previousBillableUnits,
  previousUnits,
  finalCalculationUnits,
  pricing,
  prorationRatio = 1,
  latestTierLocked
}) {
  const currentUnits = measurement.value + previousUnits;
  let nonBillableUnitsCount = measurement.value - billableAmount;
  const tiers = getUsageTiers(pricing, currentUnits, finalCalculationUnits, latestTierLocked);
  const isVolume = isVolumePricing2(pricing);
  const billableUnitsMinimumAmount = isPositive(measurement.value) ? 0 : measurement.value;
  return tiers.reduce((accumulatedPrices, currentTier, tierIndex) => {
    const { from, to, price, chunkSize, volumeIndex } = currentTier;
    const calculatedTierIndex = isVolume && isNotNullOrUndefined23(volumeIndex) ? volumeIndex : tierIndex;
    const isChargeableVolume = !isVolume || isBetweenInclusive4(finalCalculationUnits, from, to);
    const isPreviousTier = to <= previousUnits;
    const isFutureTier = from > currentUnits;
    if ((isPreviousTier || isFutureTier) && !isNegative2(measurement.value)) {
      return accumulatedPrices;
    }
    const tierCalculationStartUnits = getCalculationStartUnits(previousUnits, tiers, tierIndex);
    const nonNegativeCurrentUnits = Math.max(currentUnits, 0);
    const nonInfiniteTierLimit = Math.min(to, nonNegativeCurrentUnits);
    const tierUnits = Math.min(nonInfiniteTierLimit - tierCalculationStartUnits, measurement.value);
    const tierBillableUnits = Math.max(tierUnits - nonBillableUnitsCount, billableUnitsMinimumAmount);
    const amountDeducted = Math.max(tierUnits - tierBillableUnits, tierUnits);
    nonBillableUnitsCount = Math.max(nonBillableUnitsCount - amountDeducted, 0);
    const previouslyChargedUnitsCount = getUnitsAmount(previousBillableUnits, chunkSize);
    const overallChargeableUnitsCount = getUnitsAmount(previousBillableUnits + tierBillableUnits, chunkSize);
    const currentChargeableUnitsCount = overallChargeableUnitsCount - previouslyChargedUnitsCount;
    const tierPrice = isChargeableVolume ? price : 0;
    const calculatedPrice = currentChargeableUnitsCount * tierPrice;
    const proratedPrice = calculatedPrice * prorationRatio;
    return [
      ...accumulatedPrices,
      {
        measuredPrice: {
          units: tierUnits,
          billableUnits: tierBillableUnits,
          nonBillableUnits: tierUnits - tierBillableUnits,
          price: proratedPrice,
          currency
        },
        tierIndex: calculatedTierIndex
      }
    ];
  }, []);
}
function getCalculationStartUnits(previousUnits, tiers, currentIndex) {
  const previousTier = tiers.at(currentIndex - 1);
  if (isNullOrUndefined23(previousTier)) {
    return previousUnits;
  }
  const { to: previousTo } = previousTier;
  const { from: currentFrom } = requiredAt2(tiers, currentIndex, "tiers");
  const isNonGappedTiers = previousTo === currentFrom;
  const fromLimit = isNonGappedTiers ? currentFrom : currentFrom - 1;
  return Math.max(previousUnits, fromLimit);
}
function getUnitsAmount(units, chunkSize) {
  if (isNullOrUndefined23(chunkSize)) {
    return units;
  }
  return getChunksAmount(units, chunkSize);
}
function getChunksAmount(units, chunkSize) {
  const nonCeiledChunkCount = safeDivision4(units, chunkSize);
  const ceiledChunkCount = Math.ceil(nonCeiledChunkCount);
  return Math.max(ceiledChunkCount, 0);
}
function isVolumePricing2(pricing) {
  return isTiersPricing(pricing) || isTieredSubscriptionPricing(pricing) ? pricing.priceConfiguration.isVolume : false;
}

// src/usage-variant-charges-calculator/measured-prices-sum-builder.ts
function sumMeasuredPriceBreakdown(measuredPriceBreakdown, currency) {
  return measuredPriceBreakdown.reduce(
    (accumulatedPrices, currentPrice) => accumulateMeasuredPrice(accumulatedPrices, currentPrice, currency),
    buildEmptyMeasuredPrice(currency)
  );
}
function accumulateMeasuredPrice(accumulatedPrices, currentPrice, currency) {
  return {
    billableUnits: accumulatedPrices.billableUnits + currentPrice.measuredPrice.billableUnits,
    nonBillableUnits: accumulatedPrices.nonBillableUnits + currentPrice.measuredPrice.nonBillableUnits,
    currency,
    price: accumulatedPrices.price + currentPrice.measuredPrice.price,
    units: accumulatedPrices.units + currentPrice.measuredPrice.units
  };
}
function buildEmptyMeasuredPrice(currency) {
  return {
    billableUnits: 0,
    nonBillableUnits: 0,
    currency,
    price: 0,
    units: 0
  };
}

// src/usage-variant-charges-calculator/usage-variant-charges-calculator.ts
function getNonCommitmentUsageVariantCharges(lineItemProductVariant, measurements, finalCalculationUnits) {
  return getUsageVariantCharges(lineItemProductVariant, measurements, 0, 0, finalCalculationUnits, true);
}
function getPreCommitmentUsageVariantCharges(lineItemProductVariant, measurements, finalCalculationUnits) {
  return getUsageVariantCharges(lineItemProductVariant, measurements, 0, 0, finalCalculationUnits, false);
}
function getPreCommitmentCrossingMeasurementUsageVariantCharges(lineItemProductVariant, measurements, initialNonBillableUnits, finalCalculationUnits) {
  return getUsageVariantCharges(
    lineItemProductVariant,
    measurements,
    initialNonBillableUnits,
    0,
    finalCalculationUnits,
    false
  );
}
function getPostCommitmentUsageVariantCharges(lineItemProductVariant, measurements, initialUnits, initialBillableUnits, finalCalculationUnits) {
  return getUsageVariantCharges(
    lineItemProductVariant,
    measurements,
    initialUnits,
    initialBillableUnits,
    finalCalculationUnits,
    true
  );
}
function getUsageVariantCharges(lineItemProductVariant, measurements, initialUnits, initialBillableUnits, finalCalculationUnits, isBillable) {
  const { productVariantId, meter, pricing } = lineItemProductVariant;
  const { meterId } = meter ?? {};
  validateNotNullOrUndefined7(productVariantId, "productVariant.productVariantId");
  validateNotNullOrUndefined7(meterId, "meter.meterId");
  let currentUnits = initialUnits;
  let currentBillableUnits = initialBillableUnits;
  return measurements.map((measurement) => {
    const measuredPriceBreakdown = getMeasuredPricesBreakdown({
      billableAmount: isBillable ? measurement.value : 0,
      currency: "USD",
      measurement,
      previousBillableUnits: currentBillableUnits,
      previousUnits: currentUnits,
      finalCalculationUnits,
      pricing
    });
    const summedMeasuredPrices = sumMeasuredPriceBreakdown(measuredPriceBreakdown, "USD");
    currentUnits += summedMeasuredPrices.units;
    currentBillableUnits += summedMeasuredPrices.billableUnits;
    return buildUsageVariantCharge(
      measurement,
      productVariantId,
      meterId,
      summedMeasuredPrices,
      measuredPriceBreakdown
    );
  });
}
function buildUsageVariantCharge(measurement, productVariantId, meterId, measuredPrice, measuredPriceBreakdown) {
  const { id, period } = measurement;
  return {
    meterId,
    productVariantId,
    measurementId: id,
    measuredPrice,
    measuredPriceBreakdown,
    period,
    timeSlot: {
      day: period.startTime.getDate(),
      month: period.startTime.getMonth(),
      year: period.startTime.getFullYear()
    }
  };
}

// src/metered-seats/is-metered-seat-variant.ts
import { PRICING_MODELS as PRICING_MODELS8 } from "@vy/types";
import { isNotNullOrUndefined as isNotNullOrUndefined24 } from "@vy/utils";
function isMeteredSeatVariant(variant) {
  return variant.pricing.pricingModel === PRICING_MODELS8.Subscription && isNotNullOrUndefined24(variant.meterId);
}

// src/metered-seats/resolve-metered-seat-counts.ts
import { AGGREGATION_OPERATORS as AGGREGATION_OPERATORS2 } from "@vy/types";
async function resolveMeteredSeatCounts(variants, accountId, customerId, period, readMeasurementValuesForPeriod) {
  const seatCounts = /* @__PURE__ */ new Map();
  const meteredVariants = variants.filter(isMeteredSeatVariant);
  const entries = await Promise.all(
    meteredVariants.map(async (variant) => {
      const measurements = await readMeasurementValuesForPeriod(accountId, variant.meterId, customerId, period);
      if (measurements.length === 0) {
        return null;
      }
      const operator = variant.meter?.aggregationMethod.operator ?? AGGREGATION_OPERATORS2.Last;
      const aggregatedValue = aggregateMeasurements(measurements, operator);
      return { variantId: variant.id, seatCount: Math.ceil(aggregatedValue) };
    })
  );
  for (const entry of entries) {
    if (entry) {
      seatCounts.set(entry.variantId, entry.seatCount);
    }
  }
  return seatCounts;
}
function aggregateMeasurements(measurements, operator) {
  switch (operator) {
    case AGGREGATION_OPERATORS2.Max:
      return Math.max(...measurements.map((m) => m.value));
    case AGGREGATION_OPERATORS2.Min:
      return Math.min(...measurements.map((m) => m.value));
    case AGGREGATION_OPERATORS2.Sum:
    case AGGREGATION_OPERATORS2.Distinct:
      return measurements.reduce((sum, m) => sum + m.value, 0);
    case AGGREGATION_OPERATORS2.Count:
      return measurements.length;
    case AGGREGATION_OPERATORS2.Average: {
      const total = measurements.reduce((sum, m) => sum + m.value, 0);
      return total / measurements.length;
    }
    case AGGREGATION_OPERATORS2.First:
      return measurements.reduce(
        (earliest, m) => m.periodStartTime < earliest.periodStartTime ? m : earliest
      ).value;
    case AGGREGATION_OPERATORS2.Last:
    default:
      return measurements.reduce(
        (latest, m) => m.periodStartTime > latest.periodStartTime ? m : latest
      ).value;
  }
}

// src/products-filter/is-credit-purchase-product.ts
import { isNotNullOrUndefined as isNotNullOrUndefined25 } from "@vy/utils";
function isCreditPurchaseProduct(product) {
  return isNotNullOrUndefined25(product.creditPurchaseConfig);
}
export {
  CommercialTermsPriceEstimator,
  FORMAT_PRICE_MAXIMUM_FRACTION_DIGITS,
  FORMAT_PRICE_MINIMUM_FRACTION_DIGITS,
  FixedOneTimePriceEstimator,
  IdShortener,
  POST_COMMITMENT_FLAG,
  PRICE_ZERO_THRESHOLD,
  PRODUCT_TYPES,
  PerUnitItemCalculator,
  RefundPriceEstimator,
  SubscriptionPriceEstimator,
  TiersItemCalculator,
  UsageProductPriceEstimator,
  UsageVariantCalculator,
  applyDiscount,
  arseIntegrationConfigurationFromDb,
  buildAnrokIntegrationEntityWithMetadata,
  buildBaseIntegrationEntity,
  buildContractWithoutPlan,
  buildCurrencyLevel,
  buildDueDate,
  buildEmptyRevenueBreakdown,
  buildErpIntegrationEntity,
  buildInvoiceReviewStatus,
  buildIssueAndDueDates,
  buildIssuedAtNow,
  buildLineItemRevenueBreakdown,
  buildLineItemRevenueBreakdownWithDiscount,
  buildManualRenewContract,
  buildNewSystemEvent,
  buildStripePaymentEntity,
  calculateBillingCyclesInPeriod,
  calculateDiscountedPrice,
  calculateFixedProduct,
  calculateInnerPeriods,
  calculatePerUnitMeasurementsPrices,
  calculateProrationRatio,
  calculateTiersMeasurementsPrices,
  calculateTiersPrice,
  calculateTiersUnitNumber,
  calculateUsageLineItemDiscountPrice,
  calculateUsageProductVariant,
  checkIsTemporalAggregationOperator,
  compareUsageVariantCharges,
  convertCurrencyToUsd,
  convertPriceFromSubunits,
  convertPriceToSubunits,
  estimatePlan,
  extractLineItemId,
  filterCommercialTermsProducts,
  filterCommissionProducts,
  filterCommitmentLineItems,
  filterCommitmentProducts,
  filterFixedLineItems,
  filterFixedProducts,
  filterFixedRevisionedLineItems,
  filterOneTimeProducts,
  filterOutCommissionProducts,
  filterPeriodMeasurements,
  filterProductLineItems,
  filterPromotionLineItems,
  filterPromotionProducts,
  filterPromotionRevisionedLineItems,
  filterSubscriptionProducts,
  filterUsageLineItems,
  filterUsageProducts,
  filterUsageRevisionedLineItems,
  findCrmEntityInIntegrationEntities,
  findErpEntityInIntegrationEntities,
  findIntegrationByProviderInEntities,
  findLimitCrossingMeasurement,
  findManyPaymentEntityInIntegrationEntities,
  findPaymentEntityInIntegrationEntities,
  findRequiredIntegrationByProviderInEntities,
  findRequiredLimitCrossingMeasurement,
  formatPrice,
  formatRevSharePrice,
  formatUnitPrice,
  fromDbFilter,
  generateInvoiceNumber,
  getActiveCommitment,
  getActiveProductConfigName,
  getActiveProductConfigPricing,
  getActiveVariant,
  getActiveVariants,
  getBillingCycle,
  getBillingCycleByStartDate,
  getBillingCyclesPerDurationInMonths,
  getBillingIntervalInMonths,
  getBillingIntervalsInYear,
  getCommitmentAdjustedMeasurements,
  getCommitmentTiers,
  getCommitmentUsageProducts,
  getContractFirstBillingCycle,
  getCrossingCommitmentMeasurement,
  getCurrencySymbol,
  getCurrentInvoiceBillingCycle,
  getDefaultIntegrationConfiguration,
  getDiscountConfiguration,
  getEmptyMonthlyRevenue,
  getErpDisplayId,
  getFieldMappingByEntity,
  getFixedChargePrice,
  getFixedChargePriceForAllIntervals,
  getFixedPricingModels,
  getFixedProductPricingModel,
  getFormattedDiscount,
  getFormattedDiscountForProduct,
  getInvoiceEmailRecipients,
  getIssueDay,
  getLastContractDay,
  getLatestActiveProductConfig,
  getLatestProductConfigByCreatedAt,
  getLineItemDiscountByPricing,
  getMeasuredPricesBreakdown,
  getNonCommitmentUsageVariantCharges,
  getParsedIntegrationConfiguration,
  getPaymentDueDay,
  getPerUnitProductVariants,
  getPermissionsByRoles,
  getPostCommitmentUsageVariantCharges,
  getPreCommitmentCrossingMeasurementUsageVariantCharges,
  getPreCommitmentUsageVariantCharges,
  getPricingDiscountConfiguration,
  getProductAlignedInnerPeriod,
  getProductConfigByStartDate,
  getProductTitle,
  getProductType,
  getReportDefaultTemplate,
  getReportsBucketFilePath,
  getSplitCommitmentCrossingMeasurements,
  getStripeClient,
  getStripeDefaultConfiguration,
  getStripePaymentLink,
  getTierFlatRate,
  getTierUsagePrice,
  getTiersFittingMeasurements,
  getTiersProductVariants,
  getTotalCommitment,
  getUniqueProductConfigPeriods,
  getUsagePricingModels,
  getUsageTiers,
  getYearlyAlignedInnerPeriod,
  groupMeasurementsByBillingCycle,
  hashWithTimestamp,
  isAnonymousCustomerNotification,
  isBillingIntervalYearlyAligned,
  isCommercialTermsPricing,
  isCommercialTermsProduct,
  isCommercialTermsProductOnly,
  isCommercialTermsProductVariant,
  isCommercialTermsWithoutScheduledInstallments,
  isCommissionFixedLineItem,
  isCommissionProduct,
  isCommissionProductOnly,
  isCommissionProductVariant,
  isCommitmentCrossedNotification,
  isCommitmentLineItem,
  isCommitmentProduct,
  isCommitmentUsageProduct,
  isConsumingUsageProduct,
  isContractLengthProduct,
  isCreditInvoice,
  isCreditProduct,
  isCreditPromotionConfiguration,
  isCreditPromotionConfigurationInput,
  isCreditPurchaseProduct,
  isCustomerPortalLinkSentNotification,
  isFixedDeductionLineItem,
  isFixedDeductionProductOnly,
  isFixedDeductionProductVariant,
  isFixedDiscount,
  isFixedLineItem,
  isFixedOneTimeProductOnly,
  isFixedPricing,
  isFixedProduct,
  isFixedPromotionRevisionedLineItem,
  isFixedRevisionedLineItem,
  isFlatRatePricing,
  isInvoiceApprovedNotification,
  isLineItemDisabled,
  isLineItemEnabled,
  isLineItemWithUnits,
  isMeteredCreditLineItem,
  isMeteredCreditProductOnly,
  isMeteredCreditProductVariant,
  isMeteredSeatVariant,
  isMonthEndReportNotification,
  isNewCustomerWithoutContractNotification,
  isNonCommitmentUsageProduct,
  isNonRelativeFixedLineItem,
  isNonRelativisticFixedProduct,
  isNotMeteredCreditProductOnly,
  isNotPromotionProduct,
  isOneTimePricing,
  isOneTimeProduct,
  isOneTimeProductVariant,
  isOverageNotification,
  isPerUnitLineItem,
  isPerUnitPricing,
  isPerUnitProductVariant,
  isPerUnitUsageProductOnly,
  isPercentageDeductionLineItem,
  isPercentageDeductionProductOnly,
  isPercentageDeductionProductVariant,
  isPercentageDiscount,
  isPercentagePromotionConfiguration,
  isPercentagePromotionConfigurationInput,
  isPercentagePromotionRevisionedLineItem,
  isProductLineItem,
  isPromotionLineItem,
  isPromotionProduct,
  isRefundInvoice,
  isRefundProduct,
  isRefundProductOnly,
  isRefundProductVariant,
  isRevSharePricing,
  isRevShareProductOnly,
  isRevShareProductVariant,
  isScheduledInstallmentsProduct,
  isShouldCreateSeparateInvoiceInvoiceProduct,
  isSingleVariantProduct,
  isSubscriptionPricing,
  isSubscriptionProduct,
  isSubscriptionProductOnly,
  isSubscriptionProductVariant,
  isTierCrossedNotification,
  isTierLockedProductVariant,
  isTieredLockedLineItem,
  isTieredSubscriptionPricing,
  isTieredSubscriptionProductVariant,
  isTieredSubscriptionUsageProductOnly,
  isTiersFlatRateProduct,
  isTiersPricing,
  isTiersProductVariant,
  isTiersUsageLineItem,
  isTiersUsageProductOnly,
  isTiersVolumeProduct,
  isTiersVolumeProductVariant,
  isTrialPromotionConfiguration,
  isTrialPromotionConfigurationInput,
  isUnchargedEventsNotification,
  isUnlimitedDate,
  isUnlimitedDuration,
  isUnsyncedStripeInvoice,
  isUpcomingRenewalNotification,
  isUpfrontPayProduct,
  isUsageLineItem,
  isUsageOrCommitmentLineItem,
  isUsagePricing,
  isUsageProduct,
  isUsageProductVariant,
  isUsageRevisionedLineItem,
  isVariantOfPricingModel,
  isVolumeBasedTiersLineItem,
  isVolumePricing2 as isVolumePricing,
  mergeIntegrationEntitiesWithFallback,
  parseCustomFields,
  resolveEffectiveProducts,
  resolveMeteredSeatCounts,
  sortMeasurementsByDate,
  sortMeasurementsByStartDate,
  sortProductConfigsByCreatedAt,
  sortProductConfigsByStartDate,
  splitPriceIntoParts,
  sumMeasuredPriceBreakdown,
  unsetPlanProductDates,
  unsetProductDatesProcessor,
  usageProductEmitsFixedCharges,
  validateLineItemWithUnits,
  validatePerUnitPricing,
  validateUsagePricing,
  validateUsageProduct
};
